// /app/frontend/src/pages/Ruleta.jsx
// RULETA PREMIUM - Solo visual + animación
// GATING: NO lee Firestore hasta que el usuario esté autenticado
// El resultado SIEMPRE viene del backend
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { subscribeToSystemStatus } from '../lib/systemStatus';
import { listenSegmentos } from '../lib/segmentos';
import { listenXpCanjes } from '../lib/xpCanjes';
import { useAuth } from '../context/AuthContext';
import { userFetch, getUserIdToken } from '../lib/userFetch';
import SocialProofTicker from '../components/SocialProofTicker';
import '../styles/ruleta.css';

// URL del backend
const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://giroexpress-ruleta-backend.onrender.com';
console.log('[API] base usada', API_URL);

// Debug logs opt-in: activa con `localStorage.setItem('ruleta-debug','1')` en consola
const DEBUG_LOGS = (() => {
  try { return localStorage.getItem('ruleta-debug') === '1'; } catch { return false; }
})();
const dlog = (...args) => { if (DEBUG_LOGS) console.log(...args); };

// =============================================================================
// MAPA VISUAL DINÁMICO
// =============================================================================
// La ruleta renderiza y calcula el giro desde la misma lista elegible de
// Firestore. La flecha externa permanece fija en 12:00.
// =============================================================================
const DYNAMIC_WHEEL_PALETTE = [
  '#6d28d9', '#16a34a', '#f97316', '#facc15',
  '#0891b2', '#db2777', '#4f46e5', '#dc2626',
  '#0f766e', '#7c3aed', '#ca8a04', '#1d4ed8',
  '#be123c', '#9333ea', '#ea580c', '#2563eb'
];
const PUBLIC_WHEEL_IMAGE_BASE_PERCENT = 9.2;

function toVisualNumber(value, fallback) {
  if (value === undefined || value === null || value === '') return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function toVisualScale(value, fallback = 1) {
  const parsed = toVisualNumber(value, fallback);
  return Math.min(Math.max(parsed, 0.2), 4);
}

function hasVisualValue(value) {
  return value !== undefined && value !== null && value !== '';
}

function toWheelPercentPosition(value, fallback = 50) {
  const numeric = toVisualNumber(value, fallback / 100);
  const percent = Math.abs(numeric) <= 1 ? numeric * 100 : numeric;
  return Math.max(6, Math.min(94, percent));
}

function getSegmentImagePositionMode(seg) {
  const explicitMode = String(seg?.segment_image_position_mode || '').trim().toLowerCase();
  if (explicitMode === 'attached' || explicitMode === 'auto' || explicitMode === 'free') return explicitMode;
  return hasVisualValue(seg?.segment_image_free_x) && hasVisualValue(seg?.segment_image_free_y) ? 'free' : 'attached';
}

function normalizeHexColor(color, fallback) {
  const raw = String(color || '').trim();
  const shortHex = raw.match(/^#([0-9a-f]{3})$/i);
  if (shortHex) {
    const [, value] = shortHex;
    return `#${value.split('').map(char => char + char).join('')}`;
  }
  return /^#[0-9a-f]{6}$/i.test(raw) ? raw : fallback;
}

function adjustHexColor(color, amount) {
  const normalized = normalizeHexColor(color, '#7c3aed').slice(1);
  const clamp = value => Math.max(0, Math.min(255, value));
  const channels = [0, 2, 4].map(start => parseInt(normalized.slice(start, start + 2), 16));
  const adjusted = channels.map(channel => {
    const next = amount >= 0
      ? channel + ((255 - channel) * amount)
      : channel * (1 + amount);
    return clamp(Math.round(next)).toString(16).padStart(2, '0');
  });
  return `#${adjusted.join('')}`;
}

function getSegmentColor(seg, index) {
  const fallback = DYNAMIC_WHEEL_PALETTE[index % DYNAMIC_WHEEL_PALETTE.length];
  return normalizeHexColor(seg.segment_color || seg.color_segmento || seg.color, fallback);
}

function normalizeSegmentsInput(segmentos) {
  if (Array.isArray(segmentos)) return segmentos;
  return Object.entries(segmentos || {}).map(([id, data]) => ({
    id,
    ...(data || {})
  }));
}

function getOrderedVisibleSegments(segmentos) {
  return normalizeSegmentsInput(segmentos)
    .filter(seg => /^segmento_\d+$/.test(String(seg.id || '')))
    .filter(seg => seg.visible_en_ruleta !== false)
    .sort((a, b) => Number(String(a.id).replace('segmento_', '')) - Number(String(b.id).replace('segmento_', '')))
}

function getOrderedEligibleSegments(segmentos) {
  return getOrderedVisibleSegments(segmentos)
    .filter(seg => seg.activo === true && seg.emergencia_apagado !== true)
}

function dynamicWheelPolar(cx, cy, radius, degrees) {
  const radians = degrees * Math.PI / 180;
  return {
    x: cx + Math.sin(radians) * radius,
    y: cy - Math.cos(radians) * radius
  };
}

function dynamicWheelSectorPath(start, end) {
  const cx = 100;
  const cy = 100;
  const radius = 94;
  const p1 = dynamicWheelPolar(cx, cy, radius, start);
  const p2 = dynamicWheelPolar(cx, cy, radius, end);
  const largeArc = end - start > 180 ? 1 : 0;
  return `M ${cx} ${cy} L ${p1.x.toFixed(3)} ${p1.y.toFixed(3)} A ${radius} ${radius} 0 ${largeArc} 1 ${p2.x.toFixed(3)} ${p2.y.toFixed(3)} Z`;
}

function getSegmentGeometry(index, totalSegments) {
  const safeTotal = Math.max(totalSegments, 1);
  const step = 360 / safeTotal;
  const centerAngle = index * step;
  return {
    step,
    centerAngle,
    startAngle: centerAngle - step / 2,
    endAngle: centerAngle + step / 2
  };
}

function getProportionalWheelImageScale(step, scaleMultiplier) {
  const imageRadiusPercent = 29;
  const arcWidth = 2 * Math.PI * imageRadiusPercent * (step / 360);
  const baseImageSize = arcWidth * 0.72;
  const maxImageSize = Math.min(24, arcWidth * 2.4);
  const finalImageSize = Math.max(3.2, Math.min(maxImageSize, baseImageSize * scaleMultiplier));
  return finalImageSize / PUBLIC_WHEEL_IMAGE_BASE_PERCENT;
}

function DynamicWheelVisual({ segmentos, currentRotation }) {
  const visualSegments = getOrderedVisibleSegments(segmentos);
  const totalSegments = Math.max(visualSegments.length, 1);

  return (
    <div className="dynamic-wheel-visual" data-current-rotation={Math.round(currentRotation % 360)} aria-hidden="true">
      <svg className="dynamic-wheel-segments-svg" viewBox="0 0 200 200" role="img" aria-label="Ruleta de premios">
        <defs>
          <mask id="dynamic-wheel-center-mask">
            <rect x="0" y="0" width="200" height="200" fill="white" />
            <circle cx="100" cy="100" r="18" fill="black" />
          </mask>
          <linearGradient id="dynamic-wheel-segment-shine" x1="12%" y1="4%" x2="88%" y2="92%">
            <stop offset="0%" stopColor="rgba(255,255,255,0.14)" />
            <stop offset="28%" stopColor="rgba(255,255,255,0.04)" />
            <stop offset="58%" stopColor="rgba(255,255,255,0)" />
            <stop offset="100%" stopColor="rgba(255,255,255,0.05)" />
          </linearGradient>
          <radialGradient id="dynamic-wheel-global-gloss" cx="42%" cy="20%" r="72%">
            <stop offset="0%" stopColor="rgba(255,255,255,0.10)" />
            <stop offset="24%" stopColor="rgba(255,255,255,0.03)" />
            <stop offset="56%" stopColor="rgba(255,255,255,0)" />
            <stop offset="100%" stopColor="rgba(255,255,255,0.04)" />
          </radialGradient>
          {visualSegments.map((seg, index) => {
            const base = getSegmentColor(seg, index);
            const light = adjustHexColor(base, 0.10);
            const mid = adjustHexColor(base, 0.03);
            const dark = adjustHexColor(base, -0.30);
            const rim = adjustHexColor(base, 0.08);
            return (
              <radialGradient
                id={`dynamic-wheel-segment-gloss-${index}`}
                key={`gradient-${seg.id}`}
                gradientUnits="userSpaceOnUse"
                cx="100"
                cy="100"
                r="96"
              >
                <stop offset="0%" stopColor={dark} />
                <stop offset="42%" stopColor={base} />
                <stop offset="78%" stopColor={mid} />
                <stop offset="92%" stopColor={light} />
                <stop offset="100%" stopColor={rim} />
              </radialGradient>
            );
          })}
        </defs>
        <g className="dynamic-wheel-segments" mask="url(#dynamic-wheel-center-mask)">
          {visualSegments.map((seg, index) => {
            const { startAngle, endAngle } = getSegmentGeometry(index, totalSegments);
            const path = dynamicWheelSectorPath(startAngle, endAngle);
            return (
              <React.Fragment key={seg.id}>
                <path
                  className="dynamic-wheel-segment"
                  d={path}
                  fill={`url(#dynamic-wheel-segment-gloss-${index})`}
                />
                <path
                  className="dynamic-wheel-segment-shine"
                  d={path}
                  fill="url(#dynamic-wheel-segment-shine)"
                />
              </React.Fragment>
            );
          })}
        </g>
        <circle
          className="dynamic-wheel-global-gloss"
          cx="100"
          cy="100"
          r="94"
          fill="url(#dynamic-wheel-global-gloss)"
          mask="url(#dynamic-wheel-center-mask)"
        />
      </svg>

      <div className="dynamic-wheel-images-layer">
        {visualSegments.map((seg, index) => {
          const imageUrl = String(seg.segment_image_url || '').trim();
          if (!imageUrl) return null;

          const { centerAngle, step } = getSegmentGeometry(index, totalSegments);
          const point = dynamicWheelPolar(100, 100, 58, centerAngle);
          const imagePositionMode = getSegmentImagePositionMode(seg);
          const hasFreePosition = imagePositionMode === 'free' && hasVisualValue(seg.segment_image_free_x) && hasVisualValue(seg.segment_image_free_y);
          const offsetX = imagePositionMode === 'attached' ? toVisualNumber(seg.segment_image_offset_x, 0) : 0;
          const offsetY = imagePositionMode === 'attached' ? toVisualNumber(seg.segment_image_offset_y, 0) : 0;
          const fallbackScale = toVisualScale(seg.segment_image_scale, 1);
          const scaleMultiplier = toVisualScale(seg.segment_image_free_scale, fallbackScale);
          const renderScale = getProportionalWheelImageScale(step, scaleMultiplier);
          const baseRotation = centerAngle > 90 && centerAngle < 270 ? centerAngle + 180 : centerAngle;
          const rotationBase = toVisualNumber(seg.segment_image_free_rotation, toVisualNumber(seg.segment_image_rotation, 0));
          const left = hasFreePosition
            ? toWheelPercentPosition(seg.segment_image_free_x, 50)
            : (point.x / 2) + offsetX;
          const top = hasFreePosition
            ? toWheelPercentPosition(seg.segment_image_free_y, 50)
            : (point.y / 2) + offsetY;
          const rotation = (hasFreePosition ? 0 : baseRotation) + rotationBase;

          return (
            <span
              key={seg.id}
              className="dynamic-wheel-segment-icon has-image"
              style={{
                left: `${left}%`,
                top: `${top}%`,
                transform: `translate(-50%, -50%) scale(${renderScale.toFixed(2)}) rotate(${rotation}deg)`
              }}
            >
              <img className="dynamic-wheel-segment-img" src={imageUrl} alt="" />
            </span>
          );
        })}
      </div>

      <div className="dynamic-wheel-gaming-ring" aria-hidden="true">
        {Array.from({ length: 16 }).map((_, index) => {
          const angle = index * 22.5;
          return (
            <span
              key={index}
              style={{
                '--a': `${angle}deg`
              }}
            />
          );
        })}
      </div>

      <div className="dynamic-wheel-center" aria-label="Centro visual de la ruleta">
        <svg className="dynamic-wheel-center-symbol" viewBox="0 0 120 120" aria-hidden="true" focusable="false">
          <defs>
            <radialGradient id="wheel-center-core" cx="50%" cy="45%" r="62%">
              <stop offset="0%" stopColor="#f7d46b" />
              <stop offset="18%" stopColor="#9d4edd" />
              <stop offset="56%" stopColor="#5b1bb7" />
              <stop offset="100%" stopColor="#17072f" />
            </radialGradient>
            <linearGradient id="wheel-center-star" x1="26%" y1="18%" x2="78%" y2="88%">
              <stop offset="0%" stopColor="#fff1a8" />
              <stop offset="30%" stopColor="#f7b928" />
              <stop offset="58%" stopColor="#a855f7" />
              <stop offset="100%" stopColor="#4c1d95" />
            </linearGradient>
            <filter id="wheel-center-glow" x="-45%" y="-45%" width="190%" height="190%">
              <feGaussianBlur stdDeviation="4" result="blur" />
              <feColorMatrix in="blur" type="matrix" values="0.58 0 0 0 0.26 0 0.12 0 0 0.02 0 0 0.92 0 0.98 0 0 0 0.85 0" />
              <feMerge>
                <feMergeNode />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            <filter id="wheel-center-smoke" x="-65%" y="-65%" width="230%" height="230%">
              <feTurbulence type="fractalNoise" baseFrequency="0.018" numOctaves="2" seed="8" result="noise" />
              <feDisplacementMap in="SourceGraphic" in2="noise" scale="8" />
              <feGaussianBlur stdDeviation="5" />
            </filter>
          </defs>
          <circle className="wheel-center-smoke smoke-one" cx="60" cy="60" r="50" />
          <path className="wheel-center-smoke smoke-two" d="M23 58 C31 36 50 27 67 33 C88 40 96 57 87 76 C78 95 50 99 32 83 C23 75 19 67 23 58Z" />
          <circle className="wheel-center-aura" cx="60" cy="60" r="54" />
          <circle className="wheel-center-ring" cx="60" cy="60" r="47" />
          <circle className="wheel-center-core" cx="60" cy="60" r="38" />
          <path className="wheel-center-energy" d="M60 14 C68 32 88 33 104 42 C84 43 75 54 81 76 C68 62 51 66 34 84 C43 64 35 50 17 39 C38 41 51 34 60 14Z" />
          <path className="wheel-center-star" d="M60 22 L69.4 46.2 L95.2 47.7 L75.1 64 L82.4 88.8 L60 74.8 L37.6 88.8 L44.9 64 L24.8 47.7 L50.6 46.2 Z" />
          <path className="wheel-center-star-line" d="M60 28 L60 72 M31 50 L71 60 M89 50 L49 60 M43 82 L62 59 M77 82 L58 59" />
          <circle className="wheel-center-spark spark-one" cx="33" cy="36" r="2.1" />
          <circle className="wheel-center-spark spark-two" cx="91" cy="41" r="1.8" />
          <circle className="wheel-center-spark spark-three" cx="84" cy="85" r="1.6" />
        </svg>
      </div>
    </div>
  );
}

function getTargetAngleForSegment(winnerId, orderedSegments) {
  const totalSegments = orderedSegments.length;
  const index = orderedSegments.findIndex(seg => seg.id === winnerId);
  if (index < 0 || totalSegments <= 0) {
    console.error('[RULETA][SEGMENT_NOT_FOUND]', {
      winnerId,
      totalSegments,
      visualSegmentIds: orderedSegments.map(seg => seg.id)
    });
    return null;
  }

  const step = 360 / totalSegments;
  const position = index * step;
  return {
    targetAngle: (360 - position) % 360,
    segmentPosition: position,
    segmentIndex: index,
    totalSegments,
    step
  };
}

function calculateSpinRotation(winnerId, currentRot, lastWinnerId, orderedSegments) {
  const target = getTargetAngleForSegment(winnerId, orderedSegments);
  if (!target) return null;
  const { targetAngle, segmentPosition, segmentIndex, totalSegments, step } = target;
  
  // Vueltas aleatorias (6-10) + extra si repite ganador
  const vueltasBase = Math.floor(Math.random() * 5) + 6;
  const extraVuelta = (winnerId === lastWinnerId) ? 1 : 0;
  const totalVueltas = vueltasBase + extraVuelta;
  
  // CLAVE: El ángulo final DEBE ser exactamente targetAngle (mod 360)
  const normalizedCurrentRot = currentRot % 360;
  
  // Calcular cuánto necesitamos rotar desde la posición actual
  let rotationNeeded = targetAngle - normalizedCurrentRot;
  if (rotationNeeded <= 0) {
    rotationNeeded += 360;
  }
  
  const finalRotation = currentRot + (360 * totalVueltas) + rotationNeeded;
  
  console.log(`🎯 CÁLCULO ROTACIÓN (SVG NUEVO):`, {
    winnerId,
    segmentPosition: `${segmentPosition}°`,
    segmentIndex,
    totalSegments,
    step: `${step}°`,
    targetAngle: `${targetAngle}°`,
    vueltas: totalVueltas,
    currentRot: `${currentRot}°`,
    finalRotation: `${finalRotation}°`,
    finalMod360: `${finalRotation % 360}°`
  });
  
  return {
    finalRotation,
    vueltas: totalVueltas,
    targetAngle,
    extraVuelta: extraVuelta > 0
  };
}

// Helper XHR para bypass de interceptores de fetch que pueden leer el body
// antes de tiempo y disparar "body stream already read" (solo usado en /api/spin).
function postJsonXHR(url, payload, headers = {}) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", url, true);
    xhr.setRequestHeader("Content-Type", "application/json");

    Object.entries(headers || {}).forEach(([key, value]) => {
      if (value) xhr.setRequestHeader(key, value);
    });

    xhr.onload = () => {
      let data = {};
      try {
        data = xhr.responseText ? JSON.parse(xhr.responseText) : {};
      } catch (e) {
        console.error("[SPIN] XHR parse error:", e, xhr.responseText);
        return reject(new Error("INVALID_JSON_RESPONSE"));
      }

      if (xhr.status < 200 || xhr.status >= 300) {
        console.error("[SPIN] XHR backend error:", xhr.status, data);
        return reject({ status: xhr.status, data });
      }

      resolve(data);
    };

    xhr.onerror = () => {
      console.error("[SPIN] XHR network error");
      reject(new Error("NETWORK_ERROR"));
    };

    xhr.send(JSON.stringify(payload));
  });
}

const Ruleta = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const wheelRef = useRef(null);
  const autoSpinTriggeredRef = useRef(false);
  
  // Auth context - simplificado
  const { 
    user, 
    authReady, 
    isAuthenticated, 
    login, 
    logout, 
    isLoggingIn,
    loginError,
    clearError,
    isPreview
  } = useAuth();

  // Estados de la ruleta
  const [isSpinning, setIsSpinning] = useState(false);
  const [currentRotation, setCurrentRotation] = useState(0);
  const [prize, setPrize] = useState(null);
  const [showPrizeModal, setShowPrizeModal] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [ticketInfo, setTicketInfo] = useState({ normal: 0, golden: 0 });
  const [tiendaGlobals, setTiendaGlobals] = useState({
    tickets_normales_por_giro: 2,
    tickets_dorados_por_giro: 1,
  });
  // Canjes XP cargados desde Firestore xp_canjes
  const [xpCanjes, setXpCanjes] = useState([]);
  const [xpCanjesLoading, setXpCanjesLoading] = useState(true);
  const [xpCanjesError, setXpCanjesError] = useState(false);
  const [normalExpiry, setNormalExpiry] = useState(null);
  const [normalExpiryTick, setNormalExpiryTick] = useState(Date.now());
  const [pointsData, setPointsData] = useState({ points: 0, pointsGoal: 1000 });
  const [xpStatus, setXpStatus] = useState({
    xp: 0,
    nextReward: { requiredXp: 100, rewardName: '1 tiquete normal', missingXp: 100 },
    progressPercent: 0,
    availableRewards: [],
    loading: true,
  });
  const [showXpModal, setShowXpModal] = useState(false);
  const [extraSpinStatus, setExtraSpinStatus] = useState({
    extraSpins: 0,
    available: false,
    remainingSeconds: 0,
    loading: true,
  });
  // XP Multiplier activo (auto-aplicado al ganar segmento_05)
  // Estado leído desde backend: { multiplier, untilISO }
  const [xpMultiplierState, setXpMultiplierState] = useState({ multiplier: 0, until: null });
  const [nowTick, setNowTick] = useState(Date.now());
  const [claimingExtra, setClaimingExtra] = useState(false);
  const [claimingXpTier, setClaimingXpTier] = useState(null); // id del tier en curso
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [lastWinnerId, setLastWinnerId] = useState(null);
  const [xpToast, setXpToast] = useState(null); // {xp, key} aviso flotante "+N XP"
  const [claimToast, setClaimToast] = useState(null); // {variant, title, subtitle, icon, key}
  const [ticketTip, setTicketTip] = useState(null); // { kind: 'normal'|'golden', key }
  const ticketTipTimer = useRef(null);
  const ticketTipAutoShown = useRef(false);

  const showTicketTip = (kind, duration = 3000) => {
    if (ticketTipTimer.current) clearTimeout(ticketTipTimer.current);
    setTicketTip({ kind, key: Date.now() });
    ticketTipTimer.current = setTimeout(() => setTicketTip(null), duration);
  };
  const claimToastTimer = useRef(null);
  const pendingResultRef = useRef(null);
  
  // DEV_SPIN_BYPASS
  const [devSpinBypass, setDevSpinBypass] = useState(false);
  
  // Estado del sistema (solo se carga después de auth)
  const [systemStatus, setSystemStatus] = useState({
    isActive: true, // Default true para no bloquear
    blockReason: null,
    status: 'ready',
    maintenance: false,
    message: '',
    minVersion: 0,
    blockMessage: ''
  });

  // Segmentos (solo se cargan después de auth)
  const [segmentosData, setSegmentosData] = useState({
    segmentos: [],
    elegibles: [],
    totalSegmentos: 0,
    totalElegibles: 0,
    elegiblesIds: [],
    error: null
  });

  const orderedVisualSegments = useMemo(
    () => getOrderedVisibleSegments(segmentosData.segmentos),
    [segmentosData.segmentos]
  );

  const orderedEligibleSegments = useMemo(
    () => getOrderedEligibleSegments(segmentosData.segmentos),
    [segmentosData.segmentos]
  );

  const [showFullJson, setShowFullJson] = useState(false);
  const [debugPanelOpen, setDebugPanelOpen] = useState(() => {
    try { return localStorage.getItem('ruleta-debug-panel-open') !== 'false'; } catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem('ruleta-debug-panel-open', debugPanelOpen ? 'true' : 'false'); } catch {}
  }, [debugPanelOpen]);

  // ¿Puede ver el panel de debug de segmentos?
  // El frontend NO decide quién es admin (eso lo valida el backend con
  // require_admin). Aquí solo controlamos la visibilidad del DEBUG visual.
  // Para activarlo en una build/preview, usar la bandera explícita:
  //   REACT_APP_SHOW_DEBUG_PANEL=true
  // En producción debe quedar siempre apagada.
  const canSeeDebugPanel = (
    String(process.env.REACT_APP_SHOW_DEBUG_PANEL || '').toLowerCase() === 'true'
  );

  // =============================================================================
  // GATING: Solo cargar datos DESPUÉS de autenticación
  // =============================================================================

  // 1. Cargar config del backend (DEV_SPIN_BYPASS) - SOLO si autenticado
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const abortController = new AbortController();
    
    const loadConfig = async () => {
      try {
        const response = await fetch(`${API_URL}/api/config`, {
          signal: abortController.signal
        });
        if (response.ok) {
          const data = await response.json();
          setDevSpinBypass(data.dev_spin_bypass || false);
          if (data.dev_spin_bypass) {
            console.log('🔧 DEV_SPIN_BYPASS activo - Modo desarrollo');
          }
        }
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.error('Error loading config:', err);
      }
    };

    // Cargar globals de la tienda (vigencia y tickets-por-giro)
    const loadTiendaGlobals = async () => {
      try {
        const r = await fetch(`${API_URL}/api/tienda/config`, { signal: abortController.signal });
        if (!r.ok) return;
        const data = await r.json();
        const g = data?.config?.globals || {};
        const n = Number(g.tickets_normales_por_giro);
        const d = Number(g.tickets_dorados_por_giro);
        setTiendaGlobals({
          tickets_normales_por_giro: Number.isFinite(n) && n > 0 ? n : 2,
          tickets_dorados_por_giro: Number.isFinite(d) && d > 0 ? d : 1,
        });
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.log('[TIENDA-APP] error globals (usando fallbacks 2/1)');
      }
    };
    
    loadConfig();
    loadTiendaGlobals();
    return () => abortController.abort();
  }, [isAuthenticated]);

  // Cargar canjes XP desde Firestore (xp_canjes) en tiempo real.
  // Solo se suscribe cuando el usuario está autenticado.
  useEffect(() => {
    if (!isAuthenticated) return;
    setXpCanjesLoading(true);
    const unsub = listenXpCanjes(({ canjes, error }) => {
      setXpCanjesLoading(false);
      if (error) {
        setXpCanjes([]);
        setXpCanjesError(true);
      } else {
        setXpCanjes(canjes);
        setXpCanjesError(false);
      }
    });
    return () => { try { unsub && unsub(); } catch (_) { /* ignore */ } };
  }, [isAuthenticated]);

  // 2. Suscripción a Firebase system/config - SOLO si autenticado
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const unsubscribe = subscribeToSystemStatus((status) => {
      dlog('Firebase config update:', status);
      setSystemStatus(status);
    });
    
    return () => unsubscribe();
  }, [isAuthenticated]);

  // 3. Suscripción a Firebase ruleta_premios - SOLO si autenticado
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const unsubscribe = listenSegmentos((data) => {
      dlog('[Ruleta] Segmentos actualizados:', data);
      setSegmentosData(data);
    });
    
    return () => unsubscribe();
  }, [isAuthenticated]);

  // 4. Cargar datos del usuario - SOLO si autenticado.
  // Extraído a un ref para que el polling lo reutilice sin duplicar lógica.
  const fetchUserData = useRef(null);
  fetchUserData.current = async (signal) => {
    if (!user?.uid) return;
    try {
      console.log('[USER] loading desde backend');
      const params = new URLSearchParams();
      if (user.email) params.set('email', user.email);
      if (user.displayName) params.set('displayName', user.displayName);
      if (user.photoURL) params.set('photoURL', user.photoURL);
      const qs = params.toString();
      const url = `${API_URL}/api/user/${user.uid}${qs ? `?${qs}` : ''}`;
      const response = await userFetch(url, signal ? { signal } : undefined);
      if (!response.ok) return;
      const data = await response.json();
      if (data.success && data.user) {
        setTicketInfo({
          normal: data.user.normalTickets || 0,
          golden: data.user.goldenTickets || 0
        });
        setNormalExpiry(data.user.normalTicketsExpiry || null);
        setPointsData({
          points: data.user.points || 0,
          pointsGoal: 1000
        });
        setXpMultiplierState({
          multiplier: Number(data.user.xpMultiplier || 0) || 0,
          until: data.user.xpMultiplierUntil || null,
        });
        // Modal de bienvenida: mostrar UNA sola vez al primer login del usuario.
        // Backend setea welcomeShown=false al crear; tras cerrar el modal se
        // llama POST /api/user/{uid}/welcome-acknowledged para fijarlo en true.
        if (data.user.welcomeShown === false) {
          setShowWelcomeModal(true);
        }
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('Error loading user data:', err);
    }
  };

  useEffect(() => {
    if (!isAuthenticated || !user?.uid) return;
    const abortController = new AbortController();
    fetchUserData.current(abortController.signal);
    return () => abortController.abort();
  }, [isAuthenticated, user?.uid]);

  // ===== Polling ligero (10s) — solo cuando la pestaña está visible.
  // Refresca: GET /api/user/{uid}, /xp/status y /extra-spin/status.
  // Evita overlap con un guard inFlight. NO toca la lógica del giro.
  const pollingInFlightRef = useRef(false);
  useEffect(() => {
    if (!isAuthenticated || !user?.uid) return undefined;

    const tick = async () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      if (isSpinning) return; // no refrescar mid-giro (la respuesta del spin sincroniza)
      if (pollingInFlightRef.current) return;
      pollingInFlightRef.current = true;
      try {
        await Promise.allSettled([
          fetchUserData.current && fetchUserData.current(),
          fetchXpStatus.current && fetchXpStatus.current(),
          fetchExtraSpinStatus.current && fetchExtraSpinStatus.current(),
        ]);
      } finally {
        pollingInFlightRef.current = false;
      }
    };

    const interval = setInterval(tick, 10000);
    const onFocus = () => tick();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') tick();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [isAuthenticated, user?.uid, isSpinning]);

  // =============================================================================
  // XP MULTIPLIER ACTIVO — Tick cada 30s para refrescar contador en tiempo real
  // =============================================================================
  const xpMultiplierUntilMs = (() => {
    if (!xpMultiplierState.until) return 0;
    const t = Date.parse(xpMultiplierState.until);
    return Number.isFinite(t) ? t : 0;
  })();
  const xpMultiplierActive =
    Number(xpMultiplierState.multiplier) >= 2 &&
    xpMultiplierUntilMs > nowTick;

  useEffect(() => {
    if (!xpMultiplierActive) return;
    const interval = setInterval(() => setNowTick(Date.now()), 30 * 1000);
    return () => clearInterval(interval);
  }, [xpMultiplierActive]);

  const formatXpMultiplierRemaining = () => {
    const diffMs = Math.max(0, xpMultiplierUntilMs - nowTick);
    if (diffMs <= 0) return '0m';
    const totalMin = Math.floor(diffMs / 60000);
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
  };

  // =============================================================================
  // XP / PUNTOS ACUMULABLES - Tarjeta
  // =============================================================================
  const fetchXpStatus = useRef(null);
  fetchXpStatus.current = async () => {
    if (!user?.uid) return;
    try {
      console.log('[XP] status desde backend');
      const res = await userFetch(`${API_URL}/api/user/${user.uid}/xp/status`);
      if (!res.ok) return;
      const d = await res.json();
      setXpStatus({
        xp: d.xp || 0,
        nextReward: d.nextReward || null,
        progressPercent: d.progressPercent || 0,
        availableRewards: d.availableRewards || [],
        loading: false,
      });
    } catch (err) {
      console.error('Error XP status:', err);
    }
  };

  // Cargar al entrar + refrescar cuando cambian los puntos (ej: tras reclamo de XP)
  useEffect(() => {
    if (!isAuthenticated || !user?.uid) return;
    fetchXpStatus.current();
  }, [isAuthenticated, user?.uid, pointsData.points]);

  // Escuchar evento 'xp:credited' (disparado tras compra de tiquetes con xp_por_compra > 0).
  // Actualiza la tarjeta de XP en caliente sin esperar al próximo refetch.
  useEffect(() => {
    if (!isAuthenticated) return;
    const onXpCredited = (e) => {
      const detail = e?.detail || {};
      const xpAwarded = Number(detail.xpAwarded || 0);
      const pointsAfter = Number(detail.pointsAfter || 0);
      if (pointsAfter > 0) {
        setXpStatus((s) => ({ ...s, xp: pointsAfter }));
        setPointsData((prev) => ({ ...prev, points: pointsAfter }));
      }
      // Refrescar nextReward/progressPercent desde backend (no bloqueante)
      if (fetchXpStatus.current) fetchXpStatus.current();
      // Mostrar el toast "+N XP" reutilizando el ya existente del modal Canjear XP
      if (xpAwarded > 0) {
        showClaimToast('success', `⚡ +${xpAwarded} XP agregados`, '🎯 Te acercaste a tu próximo canje', 'xp');
      }
    };
    window.addEventListener('xp:credited', onXpCredited);
    return () => window.removeEventListener('xp:credited', onXpCredited);
  }, [isAuthenticated]);

  // =============================================================================
  // GIRO ADICIONAL DIARIO - Tarjeta
  // =============================================================================
  const fetchExtraSpinStatus = useRef(null);
  fetchExtraSpinStatus.current = async () => {
    if (!user?.uid) return;
    try {
      console.log('[EXTRA-SPIN] status desde backend');
      const res = await userFetch(`${API_URL}/api/user/${user.uid}/extra-spin/status`);
      if (!res.ok) return;
      const d = await res.json();
      setExtraSpinStatus({
        extraSpins: d.extraSpins || 0,
        available: d.available === true,
        remainingSeconds: d.remainingSeconds || 0,
        loading: false,
      });
    } catch (err) {
      console.error('Error extra-spin status:', err);
    }
  };

  // Cargar estado al entrar
  useEffect(() => {
    if (!isAuthenticated || !user?.uid) return;
    fetchExtraSpinStatus.current();
  }, [isAuthenticated, user?.uid]);

  // Auto-show de tooltips de tickets al entrar a la ruleta (solo una vez por sesión)
  useEffect(() => {
    if (!isAuthenticated) return;
    if (ticketTipAutoShown.current) return;
    ticketTipAutoShown.current = true;
    const t1 = setTimeout(() => showTicketTip('normal', 2800), 900);
    const t2 = setTimeout(() => showTicketTip('golden', 2800), 900 + 2800 + 200);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
    // eslint-disable-next-line
  }, [isAuthenticated]);

  // Cerrar tooltip de tickets al tocar fuera de los chips
  useEffect(() => {
    if (!ticketTip) return;
    const onDocClick = (e) => {
      const inside = e.target.closest && e.target.closest('.ticket-chips');
      if (!inside) {
        if (ticketTipTimer.current) clearTimeout(ticketTipTimer.current);
        setTicketTip(null);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('touchstart', onDocClick, { passive: true });
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('touchstart', onDocClick);
    };
  }, [ticketTip]);

  // Tick cada 1s para countdown en tiempo real del vencimiento de normales
  useEffect(() => {
    if (!normalExpiry || ticketInfo.normal <= 0) return;
    const id = setInterval(() => setNormalExpiryTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, [normalExpiry, ticketInfo.normal]);

  const getNormalExpiryText = () => {
    if (!ticketInfo.normal) return 'Sin normales activos';
    if (!normalExpiry) return null;
    try {
      const expiry = new Date(normalExpiry);
      if (Number.isNaN(expiry.getTime())) return null;
      const diffMs = expiry.getTime() - normalExpiryTick;
      if (diffMs <= 0) return 'Vencidos';
      const totalSec = Math.floor(diffMs / 1000);
      const h = Math.floor(totalSec / 3600);
      const m = Math.floor((totalSec % 3600) / 60);
      const s = totalSec % 60;
      const pad = (n) => String(n).padStart(2, '0');
      if (h > 0) return `Vence en ${h}h ${pad(m)}m ${pad(s)}s`;
      if (m > 0) return `Vence en ${m}m ${pad(s)}s`;
      return `Vence en ${s}s`;
    } catch {
      return null;
    }
  };


  // Countdown local: cada segundo decrementa remainingSeconds (hora servidor
  // como fuente de verdad; refresca del backend al llegar a 0)
  useEffect(() => {
    if (extraSpinStatus.available || extraSpinStatus.remainingSeconds <= 0) return;
    const tick = setInterval(() => {
      setExtraSpinStatus((s) => {
        const next = Math.max(0, s.remainingSeconds - 1);
        if (next === 0) {
          if (fetchExtraSpinStatus.current) fetchExtraSpinStatus.current();
          return { ...s, remainingSeconds: 0, available: true };
        }
        return { ...s, remainingSeconds: next };
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [extraSpinStatus.available, extraSpinStatus.remainingSeconds]);

  const handleClaimExtraSpin = async () => {
    if (!user?.uid || claimingExtra) return;
    if (!extraSpinStatus.available) return;
    if (isSpinning || isLoading) return;
    setClaimingExtra(true);
    try {
      // El giro diario NO se acumula en extraSpins: se dispara directamente.
      // Backend resetea cooldown atómicamente al consumirse.
      await handleSpin({ dailyExtra: true });
      // Marcar cooldown en UI de inmediato (el status real llegará en próximo fetch)
      setExtraSpinStatus((s) => ({
        ...s,
        available: false,
        remainingSeconds: 86400,
      }));
    } catch (err) {
      console.error('Error usando giro diario:', err);
    } finally {
      setClaimingExtra(false);
    }
  };

  // Canjear XP por premio (tabla oficial)
  const showClaimToast = (variant, title, subtitle, rewardKind = null) => {
    if (claimToastTimer.current) clearTimeout(claimToastTimer.current);
    setClaimToast({ variant, title, subtitle, rewardKind, key: Date.now() });
    claimToastTimer.current = setTimeout(() => setClaimToast(null), 3000);
  };

  const formatDeliveryDiscountExpiry = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    const day = date.getDate().toString().padStart(2, '0');
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const period = hours >= 12 ? 'pm' : 'am';
    const hour12 = hours % 12 || 12;
    return `${day} ${months[date.getMonth()]} · ${hour12}:${minutes} ${period}`;
  };

  // Determinar "kind" visual para el icono del premio
  const xpCostToKind = (xpCost) => {
    if ([100, 250].includes(xpCost)) return 'ticket';
    if (xpCost === 500) return 'spin';
    if (xpCost === 800) return 'golden';
    if ([1200, 2500, 5000, 10000].includes(xpCost)) return 'money';
    return 'xp';
  };

  // PROGRESO XP visual hacia el PRÓXIMO canje activo (tramo-a-tramo).
  // Lee canjes activos de Firestore (xp_canjes), los ordena por costo_xp asc, y:
  //   1. Busca el primer activo con costo_xp > xp_actual → ese es "goal".
  //   2. El tier anterior (mayor costo_xp <= xp_actual, o 0) es "prevGoal".
  //   3. percent = (xp - prevGoal) / (goal - prevGoal).
  //   4. Si xp >= max(costo_xp activo) → reachedMax (Máximo alcanzado).
  // Sólo cálculo visual; NO toca backend, claim, candado ni lógica de giro.
  const xpVisual = useMemo(() => {
    const xp = Number(xpStatus.xp || 0);
    const activos = (xpCanjes || [])
      .filter((c) => c && c.activo === true && Number(c.costo_xp) > 0)
      .map((c) => Number(c.costo_xp))
      .sort((a, b) => a - b);

    // Sin datos cargados aún: fallback al backend (no romper UI).
    if (activos.length === 0) {
      const fallbackGoal = Number(xpStatus?.nextReward?.requiredXp) || 100;
      const fallbackMissing = Math.max(0, fallbackGoal - xp);
      const fallbackPercent = fallbackGoal > 0
        ? Math.max(0, Math.min(100, Math.round((xp / fallbackGoal) * 100)))
        : 0;
      return { goal: fallbackGoal, missing: fallbackMissing, percent: fallbackPercent, reachedMax: false };
    }

    const maxActivo = activos[activos.length - 1];
    // Máximo alcanzado: superó todos los canjes activos.
    if (xp >= maxActivo) {
      return { goal: maxActivo, missing: 0, percent: 100, reachedMax: true };
    }
    // Próximo objetivo: primer activo con costo_xp > xp.
    const goal = activos.find((c) => c > xp) ?? maxActivo;
    // Tier anterior: mayor costo_xp <= xp, o 0 si no hay.
    const lowers = activos.filter((c) => c <= xp);
    const prevGoal = lowers.length ? lowers[lowers.length - 1] : 0;
    const span = goal - prevGoal;
    const gained = xp - prevGoal;
    const percent = span > 0
      ? Math.max(0, Math.min(100, Math.round((gained / span) * 100)))
      : 0;
    const missing = Math.max(0, goal - xp);
    return { goal, missing, percent, reachedMax: false };
  }, [xpCanjes, xpStatus.xp, xpStatus.nextReward]);

  const handleClaimXp = async (xpCost, rewardName) => {
    if (!user?.uid) return;
    if (claimingXpTier !== null) return;          // evita doble tap
    if ((xpStatus.xp || 0) < xpCost) return;      // candado: no se permite
    setClaimingXpTier(xpCost);
    try {
      const res = await userFetch(`${API_URL}/api/user/${user.uid}/xp/claim`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ xpCost }),
      });
      const d = await res.json();
      if (!res.ok || !d.success) {
        const detail = d.detail || {};
        if (detail.error === 'INSUFFICIENT_XP') {
          const falt = Math.max(0, (detail.requiredXp || xpCost) - (detail.currentXp || xpStatus.xp || 0));
          showClaimToast('warn', 'XP insuficiente', `Te faltan ${falt} XP para este canje`);
        } else {
          showClaimToast('error', 'Canje fallido', 'No se pudo completar el canje. Intenta de nuevo.');
        }
        return;
      }
      // Actualizar XP local + pointsData inmediato
      setXpStatus((s) => ({ ...s, xp: d.remainingXp }));
      setPointsData((prev) => ({ ...prev, points: d.remainingXp }));
      if (d.user) {
        setTicketInfo((t) => ({
          normal: d.user.normalTickets ?? t.normal,
          golden: d.user.goldenTickets ?? t.golden,
        }));
        if (typeof d.user.extraSpins === 'number') {
          setExtraSpinStatus((s) => ({ ...s, extraSpins: d.user.extraSpins }));
        }
      }
      // Refrescar XP status desde backend para obtener nextReward actualizado
      if (fetchXpStatus.current) fetchXpStatus.current();

      // Caso especial: canje de "1 giro adicional" (xpCost === 500)
      // → cerrar modal y disparar el giro inmediatamente.
      const kind = xpCostToKind(xpCost);
      if (kind === 'spin') {
        setShowXpModal(false);
        // pequeño delay para permitir cierre fluido del modal
        setTimeout(() => { handleSpin(); }, 120);
        return;
      }

      // Toast de éxito
      if (d.status === 'available') {
        showClaimToast('money', 'Canje exitoso', `Recibiste: ${rewardName} · Premio agregado a Mis Premios`, kind);
      } else {
        showClaimToast('success', 'Canje exitoso', `Recibiste: ${rewardName}`, kind);
      }
    } catch (err) {
      console.error('Error canjeando XP:', err);
      showClaimToast('error', 'Error de conexión', 'No se pudo completar el canje. Intenta de nuevo.');
    } finally {
      setClaimingXpTier(null);
    }
  };

  // Formatear countdown
  // - con horas: "23h 57m 42s"
  // - solo minutos: "57m 42s"
  // - solo segundos: "42s"
  const formatRemaining = (seconds) => {
    if (seconds <= 0) return '';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) return `${h}h ${m}m ${s}s`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  };

  // =============================================================================
  // LÓGICA DE SPIN
  // =============================================================================
  
  // Reglas de consumo dinámicas: tickets_normales_por_giro y tickets_dorados_por_giro
  // vienen de tienda_config/_id="default".globals (cargados en loadTiendaGlobals).
  // REGLA ESTRICTA (anti giro-infinito): solo tickets normales o dorados habilitan
  // el botón GIRAR. extraSpins/devSpinBypass NO se aceptan acá (los giros adicionales
  // tienen su propia tarjeta dedicada con flujo `daily_extra`).
  // Reglas de consumo dinámicas: tickets_normales_por_giro y tickets_dorados_por_giro
  // vienen de tienda_config/_id="default".globals (cargados en loadTiendaGlobals).
  // Frontend solo refleja la configuración; la validación y el descuento real
  // ocurren en backend (POST /api/spin) que es la única fuente de verdad.
  const requiredNormal = tiendaGlobals.tickets_normales_por_giro;
  const requiredGolden = tiendaGlobals.tickets_dorados_por_giro;
  const canAfford =
    ticketInfo.normal >= requiredNormal ||
    ticketInfo.golden >= requiredGolden;
  const hasTickets = canAfford;
  const canSpin = isAuthenticated && hasTickets && systemStatus.isActive;

  // Mensaje dinámico cuando NO se puede afford. Dos líneas según situación:
  //  L1: estado de tiquetes normales (0 vs incompletos).
  //  L2: alternativa con dorados si hay suficientes, o invitación a la tienda.
  let insufficientMsgLine1 = '';
  let insufficientMsgLine2 = '';
  if (!canAfford) {
    if (ticketInfo.normal <= 0) {
      insufficientMsgLine1 = `Necesitas ${requiredNormal} tiquetes normales para girar.`;
    } else {
      const faltantes = Math.max(0, requiredNormal - ticketInfo.normal);
      insufficientMsgLine1 = `Tienes ${ticketInfo.normal} de ${requiredNormal} tiquetes normales. Te falta${faltantes === 1 ? '' : 'n'} ${faltantes} para girar.`;
    }
    if (ticketInfo.golden >= requiredGolden) {
      insufficientMsgLine2 = `O usa ${requiredGolden} tiquete dorado para girar.`;
    } else {
      insufficientMsgLine2 = 'También puedes completar tus tiquetes desde la tienda.';
    }
  }

  const handleSpin = async (options = {}) => {
    const { dailyExtra = false } = options;
    if (!isAuthenticated) return;
    if (isSpinning || isLoading || !systemStatus.isActive) return;
    // Guard estricto: solo se permite girar con tickets reales.
    // El flujo `dailyExtra` tiene su propia validación de cooldown en backend.
    if (!dailyExtra && !hasTickets) return;
    
    setError(null);
    setMessage('');
    setPrize(null);
    setShowPrizeModal(false);
    pendingResultRef.current = null;
    setIsLoading(true);

    try {
      console.log('[SPIN] usando XHR para /api/spin');
      let result;
      try {
        // Blindaje: adjuntar Firebase ID Token vía header Authorization
        const userToken = await getUserIdToken().catch(() => null);
        const authHeaders = userToken ? { Authorization: `Bearer ${userToken}` } : {};
        result = await postJsonXHR(`${API_URL}/api/spin`, {
          userId: user.uid,
          ticketType: dailyExtra ? 'daily_extra' : (ticketInfo.golden > 0 ? 'golden' : 'normal')
        }, authHeaders);
      } catch (xhrErr) {
        // xhrErr puede ser { status, data } (error HTTP) o Error (red / parse)
        if (xhrErr && typeof xhrErr === 'object' && 'status' in xhrErr) {
          const d = xhrErr.data || {};
          const errorMsg = d.detail?.message || d.message || d.error || 'Error del servidor';
          setError(errorMsg);
        } else {
          setError('Parece que no tienes conexión a internet.\nRevisa tus datos o WiFi e intenta otra vez.');
        }
        setIsLoading(false);
        return;
      }
      console.log('[SPIN] resultado XHR', result);

      if (result.ok === false) {
        const errorMsg = result.detail?.message || result.message || result.error || 'Error del servidor';
        setError(errorMsg);
        setIsLoading(false);
        return;
      }

      if (!result.success) {
        setError(result.message || 'Error al procesar el giro');
        setIsLoading(false);
        return;
      }

      const winnerId = result.ganador?.id;
      if (!winnerId) {
        setError('Respuesta inválida del servidor');
        setIsLoading(false);
        return;
      }

      const spinCalc = calculateSpinRotation(winnerId, currentRotation, lastWinnerId, orderedVisualSegments);
      if (!spinCalc) {
        setError('El segmento ganador no está visible en la ruleta actual. Intenta recargar.');
        setIsLoading(false);
        return;
      }
      
      console.log('🎰 Sincronización:', { winnerId, ...spinCalc });

      pendingResultRef.current = {
        winnerId,
        spinId: result.spinId,
        ganador: result.ganador,
        prize: result.prize,
        beneficio_temporal: result.beneficio_temporal,
        message: result.message,
        ticketsRemaining: result.ticketsRemaining,
        user: result.user,
        pointsAwarded: result.pointsAwarded,
        xpBonus: result.xpBonus || 0,
      };

      setIsSpinning(true);
      setIsLoading(false);
      setCurrentRotation(spinCalc.finalRotation);

      setTimeout(async () => {
        const pending = pendingResultRef.current;
        if (pending) {
          // Enriquecer prize con xpBonus para que el modal lo muestre ("⭐ +X XP")
          const prizeWithBonus = pending.prize ? {
            ...pending.prize,
            beneficio_id: pending.beneficio_temporal?.beneficio_id || pending.ganador?.beneficio_id,
            porcentaje_descuento: pending.beneficio_temporal?.porcentaje_descuento,
            fecha_vencimiento: pending.beneficio_temporal?.fecha_vencimiento,
            estado_beneficio: pending.beneficio_temporal?.estado,
            codigo_asignado: pending.beneficio_temporal?.codigo_asignado === true,
            xpBonus: pending.xpBonus || 0,
          } : pending.prize;
          setPrize(prizeWithBonus);
          setMessage(pending.message);
          setShowPrizeModal(true);
          
          if (pending.ticketsRemaining) {
            setTicketInfo(pending.ticketsRemaining);
          }
          // Sincronizar extraSpins si el backend lo devolvió
          if (pending.user && typeof pending.user.extraSpins === 'number') {
            setExtraSpinStatus((s) => ({ ...s, extraSpins: pending.user.extraSpins }));
          }
          
          if (pending.pointsAwarded) {
            setPointsData(prev => ({
              ...prev,
              points: prev.points + pending.pointsAwarded
            }));
            // Suprimir el toast flotante "+N XP" cuando el premio ganado es de
            // tipo XP (segmento_03, etc.). La animación visual de XP debe
            // aparecer solo al reclamar desde "Mis Premios".
            const isXpPrize = pending.prize?.type === 'xp';
            if (!isXpPrize) {
              setXpToast({ xp: pending.pointsAwarded, key: Date.now() });
              setTimeout(() => setXpToast(null), 2200);
            }
          }
          
          // Premio ya fue guardado por el backend durante POST /api/spin
          // No duplicar escritura en frontend
          if (pending.prizeDocId) {
            console.log('🎁 Premio confirmado por backend:', pending.prizeDocId);
          }
          
          setLastWinnerId(pending.winnerId);

          // Refrescar estado del XP Multiplier desde backend (segmento_05 lo activa)
          try {
            const ru = await userFetch(`${API_URL}/api/user/${user.uid}`);
            if (ru.ok) {
              const du = await ru.json();
              if (du.success && du.user) {
                setXpMultiplierState({
                  multiplier: Number(du.user.xpMultiplier || 0) || 0,
                  until: du.user.xpMultiplierUntil || null,
                });
              }
            }
          } catch (_) { /* noop */ }
        }
        
        setIsSpinning(false);
        pendingResultRef.current = null;
        setCurrentRotation(prev => prev % 360);
      }, 10000);

    } catch (err) {
      console.error('Error en spin:', err);
      setError(err.message || 'Error al conectar con el servidor');
      setIsSpinning(false);
      setIsLoading(false);
    }
  };

  // Auto-spin disparado tras reclamar "Giro adicional" desde Mis Premios.
  // Detecta location.state.autoSpin y arranca handleSpin una sola vez,
  // cuando los datos del usuario y el sistema están listos.
  useEffect(() => {
    if (autoSpinTriggeredRef.current) return;
    if (!location.state?.autoSpin) return;
    if (!isAuthenticated || !user?.uid) return;
    if (!systemStatus.isActive) return;
    if (isSpinning || isLoading) return;
    if (!hasTickets) return;

    autoSpinTriggeredRef.current = true;
    // Limpia el flag de location para no re-disparar al re-renderizar
    navigate(location.pathname, { replace: true, state: {} });
    // Pequeño delay para asegurar que la UI esté montada
    setTimeout(() => { handleSpin(); }, 250);
  }, [location.state, isAuthenticated, user?.uid, systemStatus.isActive, hasTickets, isSpinning, isLoading]);

  // =============================================================================
  // HANDLERS
  // =============================================================================
  
  // Login handler - llama directo a login() del contexto
  const handleLogin = () => {
    login(); // El contexto ya tiene logs y manejo de errores
  };

  // === Modal para segmentos tipo mensaje ===
  // Lee SOLO los campos del panel: modal_icon, modal_title, modal_line_1, modal_line_2, modal_button_text
  // Fallbacks mínimos si el campo NO existe en el backend
  const MSG_FALLBACK = {
    title: '¡Sigue intentando!',
    line_1: '',
    line_2: '',
    button: 'Seguir jugando',
  };

  const getModalContent = () => {
    if (!prize) return { icon: '', title: '', line1: '', line2: '', button: 'Seguir jugando' };

    if (prize.isRealPrize) {
      let title = '¡Premio!';
      if (prize.isMomentoGanador) title = '¡MOMENTO GANADOR!';
      else if (prize.type === 'cash' || prize.type === 'jackpot') title = '¡Felicidades!';
      // XP bonus que viene con un premio de dinero (segmentos COP + xp_otorgada>0).
      // Solo se muestra si hay bonus > 0 (cumple regla: 0/null/'--' NO se muestran).
      const bonus = Number(prize.xpBonus || 0);
      const line2 = bonus > 0 ? `⭐ +${bonus} XP` : '';
      return {
        icon: '',
        title,
        line1: prize.displayName,
        line2,
        button: '¡Genial!',
        isReal: true,
      };
    }

    // Segmento tipo mensaje: leer directo del panel
    return {
      icon: prize.modal_icon || '',
      title: prize.modal_title || MSG_FALLBACK.title,
      line1: prize.modal_line_1 || MSG_FALLBACK.line_1,
      line2: prize.modal_line_2 || MSG_FALLBACK.line_2,
      button: prize.modal_button_text || MSG_FALLBACK.button,
      isReal: false,
    };
  };

  // Log diagnóstico: se imprime SOLO cuando cambia el userId (no en cada render)
  useEffect(() => {
    if (!authReady || !isAuthenticated || !user?.uid) return;
    dlog('🎰 SHOW WHEEL - user:', user.uid);
  }, [authReady, isAuthenticated, user?.uid]);

  // =============================================================================
  // RENDER
  // =============================================================================

  // Estado 1: Auth NO está listo - mostrar spinner corto
  if (!authReady) {
    console.log('⏳ AUTH NOT READY - Showing spinner');
    return (
      <div className="ruleta-container">
        <div className="auth-loading-screen">
          <div className="loading-spinner-large"></div>
          <p>Cargando...</p>
        </div>
      </div>
    );
  }

  // Estado 2: Auth listo pero NO autenticado - mostrar login
  if (!isAuthenticated) {
    console.log('🔓 SHOW LOGIN');
    
    return (
      <div className="ruleta-container">
        <div className="login-required-screen">
          <div className="login-wheel-stage" aria-hidden="true">
            <div className="login-wheel-glow"></div>
            <img src="/Ruleta-3D.webp" alt="Ruleta" className="wheel-preview" />
            <div className="login-particles">
              <span className="lp lp--gold lp1"></span>
              <span className="lp lp--purple lp2"></span>
              <span className="lp lp--gold lp3"></span>
              <span className="lp lp--purple lp4"></span>
              <span className="lp lp--gold lp5"></span>
              <span className="lp lp--purple lp6"></span>
              <span className="lp lp--gold lp7"></span>
              <span className="lp lp--purple lp8"></span>
              <span className="lp lp--gold lp9"></span>
              <span className="lp lp--purple lp10"></span>
              <span className="lp lp--gold lp11"></span>
              <span className="lp lp--purple lp12"></span>
              <span className="lp lp--star lp13"></span>
              <span className="lp lp--star lp14"></span>
              <span className="lp lp--star lp15"></span>
            </div>
          </div>
          <h1 className="login-title">¡Bienvenido a la Ruleta!</h1>
          <p className="login-subtitle">Inicia sesión para girar y ganar premios</p>

          <div className="login-flare" aria-hidden="true">
            <span className="login-flare__halo"></span>
            <span className="login-flare__beam"></span>
            <span className="login-flare__star"></span>
          </div>
          
          {/* Mostrar error de login si existe */}
          {loginError && (
            <div className="login-error-container">
              <div className="login-error">
                <span className="error-icon">⚠️</span>
                <p className="error-message">{loginError}</p>
                <button className="error-dismiss" onClick={clearError}>✕</button>
              </div>
              <button className="error-retry-btn" onClick={handleLogin}>
                Reintentar
              </button>
            </div>
          )}
          
          {/* Indicador de entorno */}
          {isPreview && (
            <p className="env-indicator">Entorno: Preview (popup)</p>
          )}
          
          {/* Botón de login - SIEMPRE visible */}
          <button 
            className={`google-login-btn ${isLoggingIn ? 'loading' : ''}`}
            onClick={handleLogin}
            disabled={isLoggingIn}
            data-testid="login-btn"
          >
            {isLoggingIn ? (
              <>
                <div className="btn-spinner"></div>
                Conectando...
              </>
            ) : (
              <>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Iniciar con Google
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  // Estado 3: Autenticado - Mostrar ruleta completa
  // (console.log movido a useEffect dedicado más abajo para evitar spam en re-renders)
  return (
    <div className="ruleta-container">
      {/* Prueba social visual (tarjetas flotantes). Aislado, no toca lógica. */}
      <SocialProofTicker />
      {/* Auth Header - Usuario logueado */}
      <div className="auth-header">
        <div className="user-info">
          {user.photoURL && (
            <img src={user.photoURL} alt="" className="user-avatar" />
          )}
          <span className="user-name">{user.displayName || user.email}</span>
          {xpMultiplierActive && (
            <div
              className="xp-multiplier-badge xp-multiplier-badge-hud"
              data-testid="xp-multiplier-badge-hud"
              role="status"
              aria-live="polite"
              title={`XP x${xpMultiplierState.multiplier} activo`}
            >
              <span className="xpm-bolt" aria-hidden="true">⚡</span>
              <span className="xpm-clock" aria-hidden="true">⏳</span>
              <span className="xpm-time" data-testid="xp-multiplier-remaining">
                {formatXpMultiplierRemaining()} restantes
              </span>
            </div>
          )}
          <button
            className="logout-btn-neo"
            onClick={() => setShowLogoutConfirm(true)}
            data-testid="logout-btn"
            aria-label="Salir"
          >
            <span className="logout-btn-neo__inner">
              <svg
                className="logout-btn-neo__icon"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              <span className="logout-btn-neo__text">Salir</span>
            </span>
          </button>
        </div>
      </div>

      <header className="ruleta-header">
        <button className="header-btn tienda-btn" onClick={() => navigate('/tienda')}>
          <svg className="header-btn-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="9" cy="21" r="1"/>
            <circle cx="20" cy="21" r="1"/>
            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
          </svg>
          <span>Tienda de tickets</span>
          <span className="hb-particle hb-p1" aria-hidden="true"></span>
          <span className="hb-particle hb-p2" aria-hidden="true"></span>
          <span className="hb-particle hb-p3" aria-hidden="true"></span>
          <span className="hb-particle hb-p4" aria-hidden="true"></span>
          <span className="hb-particle hb-p5" aria-hidden="true"></span>
          <span className="hb-particle hb-p6" aria-hidden="true"></span>
          <span className="hb-particle hb-p7" aria-hidden="true"></span>
          <span className="hb-particle hb-p8" aria-hidden="true"></span>
          <span className="hb-particle hb-p9" aria-hidden="true"></span>
          <span className="hb-particle hb-p10" aria-hidden="true"></span>
          <span className="hb-particle hb-p11" aria-hidden="true"></span>
          <span className="hb-particle hb-p12" aria-hidden="true"></span>
        </button>
        <div className="header-divider"></div>
        <button className="header-btn premios-btn" onClick={() => navigate('/mis-premios')}>
          <svg className="header-btn-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="8" width="18" height="14" rx="2"/>
            <path d="M12 8V22"/>
            <path d="M3 12h18"/>
            <path d="M12 8c-2-3-6-3-6 0s4 4 6 4"/>
            <path d="M12 8c2-3 6-3 6 0s-4 4-6 4"/>
          </svg>
          <span>Mis premios</span>
          <span className="hb-particle hb-p1" aria-hidden="true"></span>
          <span className="hb-particle hb-p2" aria-hidden="true"></span>
          <span className="hb-particle hb-p3" aria-hidden="true"></span>
          <span className="hb-particle hb-p4" aria-hidden="true"></span>
          <span className="hb-particle hb-p5" aria-hidden="true"></span>
          <span className="hb-particle hb-p6" aria-hidden="true"></span>
          <span className="hb-particle hb-p7" aria-hidden="true"></span>
          <span className="hb-particle hb-p8" aria-hidden="true"></span>
          <span className="hb-particle hb-p9" aria-hidden="true"></span>
          <span className="hb-particle hb-p10" aria-hidden="true"></span>
          <span className="hb-particle hb-p11" aria-hidden="true"></span>
          <span className="hb-particle hb-p12" aria-hidden="true"></span>
        </button>
      </header>

      <div className="ticket-chips">
        <div className="chip-wrap">
          <button
            type="button"
            className="chip normal chip-clickable"
            data-testid="chip-normal"
            onClick={() => showTicketTip('normal')}
            aria-label="Info tiquetes normales"
          >
            <span className="chip-ticket-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 8a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v2a2 2 0 0 0 0 4v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2a2 2 0 0 0 0-4V8z"/>
                <path d="M12 6v3"/>
                <path d="M12 12v3"/>
                <path d="M12 18v0"/>
              </svg>
            </span>
            <span>{ticketInfo.normal} normales</span>
          </button>

          {getNormalExpiryText() && (
            <div className="normal-expiry-hint" data-testid="normal-expiry-hint">
              {getNormalExpiryText()}
            </div>
          )}

          {ticketTip?.kind === 'normal' && (
            <div
              key={ticketTip.key}
              className="ticket-tooltip ticket-tooltip-normal"
              role="status"
              aria-live="polite"
              data-testid="ticket-tooltip-normal"
            >
              <span className="tt-icon" aria-hidden="true">
                {/* Reloj de arena */}
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 2h12"/><path d="M6 22h12"/><path d="M6 2l6 8 6-8"/><path d="M6 22l6-8 6 8"/>
                </svg>
              </span>
              <span className="tt-text">Los normales vencen en 24h y se consumen primero</span>
              <span className="tt-arrow" aria-hidden="true"></span>
              <span className="tt-p tt-p1" aria-hidden="true"></span>
              <span className="tt-p tt-p2" aria-hidden="true"></span>
              <span className="tt-p tt-p3" aria-hidden="true"></span>
            </div>
          )}
        </div>

        <div className="chip-wrap">
          <button
            type="button"
            className="chip golden chip-clickable"
            data-testid="chip-golden"
            onClick={() => showTicketTip('golden')}
            aria-label="Info tiquetes dorados"
          >
            <span className="chip-ticket-icon golden" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 8a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v2a2 2 0 0 0 0 4v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2a2 2 0 0 0 0-4V8z"/>
                <path d="M12 7.5l1.4 2.9 3.2.3-2.4 2.2.7 3.2L12 14.5 9.1 16.1l.7-3.2L7.4 10.7l3.2-.3L12 7.5z" fill="currentColor" stroke="none"/>
              </svg>
            </span>
            <span>{ticketInfo.golden} dorados</span>
          </button>

          {ticketTip?.kind === 'golden' && (
            <div
              key={ticketTip.key}
              className="ticket-tooltip ticket-tooltip-golden"
              role="status"
              aria-live="polite"
              data-testid="ticket-tooltip-golden"
            >
              <span className="tt-icon" aria-hidden="true">
                {/* Estrella */}
                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" stroke="currentColor" strokeWidth="1" strokeLinejoin="round">
                  <path d="M12 2.5l2.9 6.1 6.6.7-4.9 4.6 1.4 6.6L12 17l-5.9 3.5 1.4-6.6L2.5 9.3l6.6-.7L12 2.5z"/>
                </svg>
              </span>
              <span className="tt-text">Los dorados son permanentes y se consumen después</span>
              <span className="tt-arrow" aria-hidden="true"></span>
              <span className="tt-p tt-p1" aria-hidden="true"></span>
              <span className="tt-p tt-p2" aria-hidden="true"></span>
              <span className="tt-p tt-p3" aria-hidden="true"></span>
            </div>
          )}
        </div>
      </div>

      {/* Total sutil debajo de las píldoras */}
      <div className="ticket-total-hint" data-testid="ticket-total-hint">
        Total disponible: {ticketInfo.normal + ticketInfo.golden}
      </div>

      {/* Ruleta */}
      <div className="roulette-wrapper">
        <div className="prize-pointer">
          <svg
            className="pointer-premium-svg"
            viewBox="0 0 64 96"
            aria-hidden="true"
            focusable="false"
          >
            <defs>
              <linearGradient id="pointerGoldEdge" x1="12" y1="5" x2="52" y2="92" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor="#fff4a8" />
                <stop offset="18%" stopColor="#f8c83a" />
                <stop offset="48%" stopColor="#a96b08" />
                <stop offset="72%" stopColor="#ffd76a" />
                <stop offset="100%" stopColor="#6f3f07" />
              </linearGradient>
              <linearGradient id="pointerGemBody" x1="32" y1="36" x2="32" y2="86" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor="#ff8cff" />
                <stop offset="38%" stopColor="#7d24c8" />
                <stop offset="72%" stopColor="#2b0b56" />
                <stop offset="100%" stopColor="#120328" />
              </linearGradient>
              <radialGradient id="pointerGemOrb" cx="50%" cy="42%" r="58%">
                <stop offset="0%" stopColor="#fff0ff" />
                <stop offset="26%" stopColor="#d24dff" />
                <stop offset="58%" stopColor="#7a17d8" />
                <stop offset="100%" stopColor="#210041" />
              </radialGradient>
              <linearGradient id="pointerWhiteGlint" x1="25" y1="39" x2="35" y2="83" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor="rgba(255,255,255,0.9)" />
                <stop offset="48%" stopColor="rgba(255,255,255,0.28)" />
                <stop offset="100%" stopColor="rgba(255,255,255,0)" />
              </linearGradient>
              <filter id="pointerPremiumGlow" x="-40%" y="-25%" width="180%" height="160%">
                <feDropShadow dx="0" dy="5" stdDeviation="4" floodColor="#080011" floodOpacity="0.62" />
                <feDropShadow dx="0" dy="0" stdDeviation="5" floodColor="#8d2cff" floodOpacity="0.5" />
                <feDropShadow dx="0" dy="0" stdDeviation="2" floodColor="#ffe071" floodOpacity="0.5" />
              </filter>
            </defs>
            <g filter="url(#pointerPremiumGlow)">
              <path
                d="M32 92 L8 30 L23 35 L32 24 L41 35 L56 30 Z"
                fill="url(#pointerGoldEdge)"
              />
              <path
                d="M32 82 L17 40 L27 43 L32 34 L37 43 L47 40 Z"
                fill="url(#pointerGemBody)"
              />
              <path
                d="M32 92 L26.8 78 L32 83 L37.2 78 Z"
                fill="#ffe38b"
                opacity="0.95"
              />
              <path
                d="M32 35 L41 35 L36 44 L28 44 L23 35 Z"
                fill="url(#pointerGoldEdge)"
              />
              <circle cx="32" cy="18" r="15" fill="url(#pointerGoldEdge)" />
              <circle cx="32" cy="18" r="10" fill="#2b073f" opacity="0.95" />
              <circle cx="32" cy="18" r="8" fill="url(#pointerGemOrb)" />
              <circle cx="29" cy="14" r="3" fill="rgba(255,255,255,0.78)" />
              <path
                d="M31.8 38 C28.5 48 28.2 62 32 80 C35.6 62 35.3 48 32.2 38 Z"
                fill="url(#pointerWhiteGlint)"
                opacity="0.72"
              />
              <path
                d="M32 92 L8 30 L23 35 L32 24 L41 35 L56 30 Z"
                fill="none"
                stroke="#fff2a8"
                strokeWidth="1.25"
                strokeLinejoin="round"
                opacity="0.65"
              />
              <circle
                cx="32"
                cy="18"
                r="15"
                fill="none"
                stroke="#fff2a8"
                strokeWidth="1.1"
                opacity="0.8"
              />
            </g>
          </svg>
          <div className="pointer-shadow"></div>
        </div>
        <div className="wheel-visual-scale">
          <div
            ref={wheelRef}
            className={`wheel-rotating-layer${isSpinning ? ' spinning' : ''}`}
            style={{ 
              transform: `rotate(${currentRotation}deg)`,
              transition: isSpinning 
                ? 'transform 10s cubic-bezier(0.15, 0.85, 0.2, 1)' 
                : 'none'
            }}
          >
            <DynamicWheelVisual segmentos={orderedVisualSegments} currentRotation={currentRotation} />
          </div>
        </div>
      </div>

      {/* Error de la app (solo errores reales, no falta de auth) */}
      {error && (
        <div className="error-message">
          <p style={{ whiteSpace: 'pre-line' }}>{error}</p>
          <button onClick={() => setError(null)}>Intentar otra vez</button>
        </div>
      )}

      {/* Mensaje informativo desde Firebase */}
      {systemStatus.message && systemStatus.isActive && (
        <div className="system-info-banner" data-testid="system-info-banner">
          <div className="info-icon">ℹ️</div>
          <p>{systemStatus.message}</p>
        </div>
      )}

      {/* Banner de sistema bloqueado (solo si hay problema real) */}
      {!systemStatus.isActive && systemStatus.blockReason && systemStatus.blockReason !== 'loading' && (
        <div className={`system-blocked-banner ${systemStatus.blockReason}`} data-testid="system-blocked-banner">
          <div className="blocked-icon">
            {systemStatus.isInMaintenance ? '🔧' : systemStatus.isVersionBlocked ? '📲' : '⚠️'}
          </div>
          <p>{systemStatus.blockMessage}</p>
        </div>
      )}

      <div className="spin-button-container">
        {devSpinBypass && (
          <div className="dev-mode-badge">
            <span>🔧 DEV</span>
          </div>
        )}

        {(() => {
          const showBuyMore = !canAfford && systemStatus.isActive && !systemStatus.isInMaintenance;
          const buttonLabel = systemStatus.isInMaintenance
            ? 'EN MANTENIMIENTO'
            : !systemStatus.isActive
              ? 'NO DISPONIBLE'
              : showBuyMore
                ? 'COMPLETAR TIQUETES'
                : 'GIRAR';
          const handleClick = showBuyMore ? () => navigate('/tienda') : handleSpin;
          const isDisabled = isSpinning || isLoading
            || !systemStatus.isActive
            || systemStatus.isInMaintenance
            || (!showBuyMore && !canSpin);
          return (
            <button
              className={`spin-button ${isSpinning ? 'spinning' : ''} ${isDisabled ? 'disabled' : ''} ${devSpinBypass ? 'dev-mode' : ''}`}
              onClick={handleClick}
              disabled={isDisabled}
              data-testid={showBuyMore ? 'spin-button-buy-more' : 'spin-button'}
            >
              {buttonLabel}
            </button>
          );
        })()}

        {!systemStatus.isActive && systemStatus.blockReason && (
          <p className="system-msg">
            {systemStatus.isInMaintenance ? 'Vuelve pronto' : 'Sistema inactivo'}
          </p>
        )}

        {systemStatus.isActive && !canAfford && (
          <p className="no-tickets-msg" data-testid="insufficient-msg">
            <span data-testid="insufficient-msg-line1">{insufficientMsgLine1}</span>
            <br />
            <span data-testid="insufficient-msg-line2">{insufficientMsgLine2}</span>
            <br />
            <span className="buy-link" data-testid="insufficient-tienda-link" onClick={() => navigate('/tienda')}> → Completar tiquetes</span>
          </p>
        )}
      </div>

      <div className="cards-container">
        <div className="figma-cards-wrapper">
          <div
            className={`figma-card puntos-card ${xpMultiplierActive ? 'puntos-card-boosted' : ''}`}
            data-testid="puntos-acumulables-card"
            role="button"
            tabIndex={0}
            onClick={() => setShowXpModal(true)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowXpModal(true); } }}
          >
            {xpMultiplierActive && (
              <div
                className="xp-mult-header"
                data-testid="xp-multiplier-badge-card"
                role="status"
                aria-live="polite"
              >
                <div className="xp-mult-header__avatar" aria-hidden="true">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path d="M13 2L3 14h7l-1 8 10-12h-7l1-8z" fill="url(#xpMultBoltGrad)" stroke="rgba(255,255,255,0.25)" strokeWidth="0.4"/>
                    <defs>
                      <linearGradient id="xpMultBoltGrad" x1="3" y1="2" x2="20" y2="22" gradientUnits="userSpaceOnUse">
                        <stop stopColor="#FFE566"/>
                        <stop offset="0.5" stopColor="#FFD700"/>
                        <stop offset="1" stopColor="#FFA500"/>
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
                <div className="xp-mult-header__title">
                  <span className="xp-mult-header__title-pre">XP</span>
                  <span className="xp-mult-header__title-mult">X{xpMultiplierState.multiplier}</span>
                  <span className="xp-mult-header__title-post">ACTIVO</span>
                </div>
                <div className="xp-mult-header__time" aria-hidden="false">
                  <span className="xp-mult-header__hourglass" aria-hidden="true">⏳</span>
                  <span className="xp-mult-header__time-text" data-testid="xp-multiplier-remaining">
                    <span className="xp-mult-header__time-num">{formatXpMultiplierRemaining()}</span>
                    <span className="xp-mult-header__time-lbl">restantes</span>
                  </span>
                </div>
              </div>
            )}
            <div className="card-inner">
              <div className="card-icon-star">
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                  <path d="M24 4L28.5 17.5L42 22L28.5 26.5L24 40L19.5 26.5L6 22L19.5 17.5L24 4Z" fill="url(#starGradient)"/>
                  <defs>
                    <linearGradient id="starGradient" x1="6" y1="4" x2="42" y2="40" gradientUnits="userSpaceOnUse">
                      <stop stopColor="#FFE566"/>
                      <stop offset="0.5" stopColor="#FFD700"/>
                      <stop offset="1" stopColor="#FFA500"/>
                    </linearGradient>
                  </defs>
                </svg>
              </div>
              <div className="card-text-area">
                <div className="card-text-row">
                  <div className="card-text-left">
                    <h3 className="card-title-figma">Puntos<br/><span className="card-title-figma__accent">acumulables</span></h3>
                    <p className="card-subtitle-figma" data-testid="xp-next-reward">
                      {xpStatus.nextReward ? (
                        <>
                          Próximo canje:
                          <br />
                          <span className="card-subtitle-figma__reward">
                            {(() => {
                              const name = (xpStatus.nextReward.rewardName || '').toLowerCase();
                              if (name.includes('dorado')) return '🏆';
                              if (name.includes('tiquete') || name.includes('ticket')) return '🎟️';
                              if (name.includes('giro')) return '🎁';
                              if (name.includes('$') || name.includes('cop')) return '💵';
                              return '⭐';
                            })()} {xpStatus.nextReward.rewardName}
                          </span>
                        </>
                      ) : (
                        'Canjebles por tikets o premios'
                      )}
                    </p>
                    {xpStatus.nextReward && (
                      <p className="card-subtitle-figma" data-testid="xp-missing" style={{ fontSize: '0.75rem', opacity: 0.9, marginTop: '2px', fontWeight: 700, display: 'none' }}>
                        <span style={{ color: '#FFFFFF' }}>Faltan</span> <span style={{ color: '#A064FF' }}>{xpStatus.nextReward.missingXp} XP</span>
                      </p>
                    )}
                  </div>
                  <div className="card-total-actual" data-testid="xp-total-actual">
                    <span className="card-total-actual__label">Total Actual</span>
                    <span className="card-total-actual__value">
                      <span className="card-total-actual__num">{xpStatus.xp}</span>
                      <span className="card-total-actual__unit">xp</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
            {!xpVisual.reachedMax && (
              <div className="xp-missing-band" data-testid="xp-missing-band">
                <span className="xp-missing-band__label">Faltan</span>{' '}
                <span className="xp-missing-band__num">{xpVisual.missing} XP</span>
              </div>
            )}
            <div className="card-progress-area">
              <div className="progress-bar-row">
                <div className="progress-bar-figma">
                  <div className="progress-fill-figma" style={{ width: `${xpVisual.percent}%` }} data-testid="xp-progress-fill"></div>
                </div>
                <span className="progress-text-figma" data-testid="xp-progress-text">
                  {xpStatus.xp} / {xpVisual.goal} XP
                </span>
              </div>
              <div className="progress-bounds-figma" data-testid="xp-progress-bounds">
                <span className="progress-bounds-figma__min">0XP</span>
                <span className="progress-bounds-figma__max">
                  {xpVisual.goal}XP
                </span>
              </div>
            </div>
            <div className="card-glow-bottom"></div>
          </div>

          <div 
            className={`figma-card giro-card ${!extraSpinStatus.available ? 'giro-card-locked' : ''}`}
            data-testid="giro-adicional-card"
            onClick={handleClaimExtraSpin}
            style={{ cursor: extraSpinStatus.available && !claimingExtra ? 'pointer' : 'default' }}
          >
            <div className="card-inner">
              <div className="card-icon-gift">
                <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
                  <rect x="6" y="18" width="32" height="20" rx="3" stroke="white" strokeWidth="2.5" fill="none"/>
                  <rect x="10" y="10" width="24" height="10" rx="2" stroke="white" strokeWidth="2.5" fill="none"/>
                  <line x1="22" y1="10" x2="22" y2="38" stroke="white" strokeWidth="2.5"/>
                  <path d="M22 10C22 10 18 6 14 8C10 10 12 14 14 16C16 18 22 18 22 18" stroke="white" strokeWidth="2" fill="none"/>
                  <path d="M22 10C22 10 26 6 30 8C34 10 32 14 30 16C28 18 22 18 22 18" stroke="white" strokeWidth="2" fill="none"/>
                </svg>
              </div>
              <div className="card-text-area">
                <h3 className="card-title-figma">Giro adicional</h3>
                <p className="card-subtitle-figma" data-testid="giro-adicional-subtitle">
                  {extraSpinStatus.loading ? (
                    'Cargando...'
                  ) : extraSpinStatus.available ? (
                    'Disponible · Girar ahora'
                  ) : extraSpinStatus.remainingSeconds > 0 ? (
                    <span className="giro-countdown" data-testid="giro-countdown">
                      <span className="giro-countdown__label">Vuelve En</span>
                      <span className="giro-countdown__time">
                        {formatRemaining(extraSpinStatus.remainingSeconds)}
                      </span>
                    </span>
                  ) : (
                    ''
                  )}
                </p>
              </div>
            </div>
            <div className="card-bottom-text" data-testid="giro-adicional-bottom">
              {claimingExtra ? (
                <span>Girando...</span>
              ) : extraSpinStatus.available ? (
                <span>¡Disponible ahora!</span>
              ) : extraSpinStatus.remainingSeconds > 0 ? (
                <div className="giro-cooldown-pill" data-testid="giro-cooldown-pill">
                  <span className="giro-cooldown-pill__line1">
                    📆 Mañana puedes volver a girar
                  </span>
                  <span className="giro-cooldown-pill__line2">
                    Prepárate para el próximo giro
                  </span>
                </div>
              ) : (
                <span>1 cada 24 h</span>
              )}
            </div>
            <div className="card-glow-bottom orange"></div>
          </div>
        </div>
      </div>

      {/* Debug Panel - Solo visible para admin o con SHOW_DEBUG_PANEL=true */}
      {canSeeDebugPanel && (
      <div className={`segment-debug-panel ${debugPanelOpen ? 'open' : 'collapsed'}`} data-testid="segment-debug-panel">
        <div className="debug-header">
          <span className="debug-title">🔥 Firebase: ruleta_premios</span>
          <span className="debug-counter">{segmentosData.totalSegmentos} total</span>
          <button
            type="button"
            className="debug-toggle-btn"
            data-testid="debug-toggle-btn"
            onClick={() => setDebugPanelOpen(v => !v)}
            aria-label={debugPanelOpen ? 'Colapsar panel' : 'Expandir panel'}
          >
            {debugPanelOpen ? '▲' : '▼'}
          </button>
        </div>

        {debugPanelOpen && (<>
        <div className="debug-counters">
          <div className="counter-box total">
            <span className="counter-value">{segmentosData.totalSegmentos}</span>
            <span className="counter-label">Segmentos</span>
          </div>
          <div className={`counter-box elegibles ${segmentosData.totalElegibles > 0 ? 'has-elegibles' : 'no-elegibles'}`}>
            <span className="counter-value">{segmentosData.totalElegibles}</span>
            <span className="counter-label">Elegibles</span>
          </div>
        </div>

        <div className="debug-elegibles-list">
          <span className="list-label">IDs elegibles:</span>
          {segmentosData.totalElegibles > 0 ? (
            <div className="elegibles-tags">
              {segmentosData.elegiblesIds.map(id => (
                <span key={id} className="elegible-tag">{id.replace('segmento_', 'S')}</span>
              ))}
            </div>
          ) : (
            <span className="no-elegibles-msg">Ninguno</span>
          )}
        </div>

        {segmentosData.error && (
          <div className="debug-error">⚠️ {segmentosData.error}</div>
        )}

        <button className="debug-json-toggle" onClick={() => setShowFullJson(!showFullJson)}>
          {showFullJson ? '🔼 Ocultar JSON' : '🔽 Ver JSON completo'}
        </button>
        
        {showFullJson && segmentosData.segmentos.length > 0 && (
          <div className="debug-json-container">
            <pre className="debug-json-content">{JSON.stringify(segmentosData.segmentos, null, 2)}</pre>
          </div>
        )}

        {segmentosData.segmentos.length > 0 && (
          <div className="debug-mini-table">
            <div className="mini-table-header">
              <span>ID</span>
              <span>Activo</span>
              <span>Emerg.</span>
              <span>Elegible</span>
            </div>
            {segmentosData.segmentos.map(seg => (
              <div key={seg.id} className={`mini-table-row ${seg.elegible ? 'row-elegible' : 'row-not-elegible'}`}>
                <span className="seg-id">{seg.id.replace('segmento_', 'S')}</span>
                <span className={seg._activo ? 'val-true' : 'val-false'}>{seg._activo ? '✓' : '✗'}</span>
                <span className={seg._emergenciaApagado ? 'val-emergency' : 'val-ok'}>{seg._emergenciaApagado ? '⚠' : '−'}</span>
                <span className={seg.elegible ? 'val-elegible' : 'val-not-elegible'}>{seg.elegible ? '✅' : '❌'}</span>
              </div>
            ))}
          </div>
        )}
        </>)}
      </div>
      )}

      {/* Modal de Bienvenida (primera sesión) */}
      {showWelcomeModal && (
        <div
          className="prize-modal-overlay"
          data-testid="welcome-modal-overlay"
          onClick={async () => {
            setShowWelcomeModal(false);
            try {
              await userFetch(`${API_URL}/api/user/${user.uid}/welcome-acknowledged`, {
                method: 'POST'
              });
            } catch (e) {
              console.warn('No se pudo marcar bienvenida como vista:', e);
            }
          }}
        >
          <div
            className="prize-modal emotional"
            data-testid="welcome-modal"
            onClick={(e) => e.stopPropagation()}
            style={{ textAlign: 'center', maxWidth: '420px' }}
          >
            <div className="modal-icon-large" aria-hidden="true">🎁</div>
            <h2>Bienvenido a la Ruleta Giro Expréss</h2>
            <p className="prize-name" style={{ marginTop: 12 }}>
              Te regalamos tu primer giro.
            </p>
            <p className="prize-line2" style={{ marginTop: 4 }}>
              Recibiste <strong>2 tiquetes normales</strong> de bienvenida.
            </p>
            <p className="prize-line2" style={{ marginTop: 12, opacity: 0.9 }}>
              Úsalos hoy y participa por premios, XP y beneficios.
            </p>
            <p
              style={{
                fontSize: '0.75rem',
                opacity: 0.6,
                marginTop: 14,
                marginBottom: 0,
              }}
            >
              Aplican términos y condiciones.
            </p>
            <button
              className="close-modal-btn"
              data-testid="welcome-modal-close-btn"
              onClick={async () => {
                setShowWelcomeModal(false);
                try {
                  await userFetch(`${API_URL}/api/user/${user.uid}/welcome-acknowledged`, {
                    method: 'POST'
                  });
                } catch (e) {
                  console.warn('No se pudo marcar bienvenida como vista:', e);
                }
              }}
            >
              ¡A girar!
            </button>
          </div>
        </div>
      )}

      {/* Modal de Premio */}
      {showPrizeModal && prize && (() => {
        const mc = getModalContent();
        const isDeliveryDiscount = prize?.beneficio_id === 'descuento_envio';
        const formattedDiscountExpiry = formatDeliveryDiscountExpiry(prize.fecha_vencimiento);
        const discountValue = prize.porcentaje_descuento == null
          ? ''
          : String(prize.porcentaje_descuento).replace('.', ',');
        return (
          <div className="prize-modal-overlay" onClick={() => setShowPrizeModal(false)}>
            <div className={`prize-modal ${isDeliveryDiscount ? 'delivery-discount-modal' : (mc.isReal ? 'real-prize' : 'emotional')}`} onClick={e => e.stopPropagation()}>
              {isDeliveryDiscount ? (
                <div className="delivery-discount-result">
                  <h2>🎉 Ganaste un descuento para envíos</h2>
                  {discountValue !== '' && (
                    <p className="delivery-discount-value">{discountValue} % de descuento</p>
                  )}
                  <p className="delivery-discount-expiry">
                    Válido hasta:<br />
                    <strong>{formattedDiscountExpiry || 'Consulta el vencimiento en Mis Premios'}</strong>
                  </p>
                  <p className="delivery-discount-instruction">
                    Tu beneficio ya fue guardado. Reclámalo en Mis Premios para obtener tu código.
                  </p>
                </div>
              ) : mc.isReal ? (
                <>
                  <div className="prize-display">
                    <span className="prize-text-large">{mc.line1}</span>
                  </div>
                  <h2>{mc.title}</h2>
                  <p className="prize-name">{mc.line1}</p>
                  {(prize.type === 'cash' || prize.type === 'jackpot') && (
                    <p className="prize-value">COP</p>
                  )}
                </>
              ) : (
                <>
                  {mc.icon && <div className="modal-icon-large">{mc.icon}</div>}
                  <h2>{mc.title}</h2>
                  {mc.line1 && <p className="prize-name">{mc.line1}</p>}
                  {mc.line2 && <p className="prize-line2">{mc.line2}</p>}
                </>
              )}
              <button className="close-modal-btn" data-testid="close-modal-btn" onClick={() => setShowPrizeModal(false)}>
                {mc.button}
              </button>
            </div>
            {/* Hint gaming "Toca para continuar" debajo del modal */}
            <div className="prize-modal-hint" aria-hidden="true">
              <span className="prize-hint-line left"></span>
              <span className="prize-hint-diamond"></span>
              <span className="prize-hint-text">Toca para continuar</span>
              <span className="prize-hint-diamond"></span>
              <span className="prize-hint-line right"></span>
            </div>
          </div>
        );
      })()}

      {/* ========== MODAL CANJEAR XP (bottom-sheet) ========== */}
      {showXpModal && (
        <div className="xp-modal-overlay" onClick={() => setShowXpModal(false)} data-testid="xp-modal-overlay">
          <div className="xp-modal-sheet" onClick={(e) => e.stopPropagation()} data-testid="xp-modal-sheet">
            <div className="xp-modal-handle"></div>

            {/* Tarjeta resumen XP (mismo diseño aprobado) */}
            <div className="xp-summary-card xp-summary-in-modal" data-testid="xp-summary-in-modal">
              <div className="xp-summary-left">
                <svg className="xp-summary-star" width="72" height="72" viewBox="0 0 48 48" fill="none">
                  <path d="M24 4L28.5 17.5L42 22L28.5 26.5L24 40L19.5 26.5L6 22L19.5 17.5L24 4Z" fill="url(#xpSumStarModal)"/>
                  <defs>
                    <linearGradient id="xpSumStarModal" x1="6" y1="4" x2="42" y2="40" gradientUnits="userSpaceOnUse">
                      <stop stopColor="#FFE566"/>
                      <stop offset="0.5" stopColor="#FFD700"/>
                      <stop offset="1" stopColor="#FFA500"/>
                    </linearGradient>
                  </defs>
                </svg>
              </div>
              <div className="xp-summary-center">
                <h3 className="xp-summary-title">
                  <span className="xp-summary-mini-star">★</span> Puntos acumulables / XP
                </h3>
                <p className="xp-summary-subtitle">
                  {xpStatus.nextReward
                    ? `Próximo canje: ${xpStatus.nextReward.rewardName}`
                    : '¡Has desbloqueado todos los canjes!'}
                </p>

                <div className="xp-summary-bottom-row">
                  <div className="xp-summary-bottom-left">
                    <div className="xp-summary-stats-row">
                      <span className="xp-summary-count">
                        {xpStatus.xp} / {xpVisual.goal} XP
                      </span>
                      <span className="xp-summary-missing">
                        {xpVisual.reachedMax ? 'Máximo alcanzado' : `Faltan ${xpVisual.missing} XP`}
                      </span>
                    </div>
                    <div className="xp-summary-progress-track">
                      <div className="xp-summary-progress-fill" style={{ width: `${xpVisual.percent}%` }}></div>
                    </div>
                  </div>
                  <div className="xp-summary-canjear-btn" aria-hidden="true">Canjear XP</div>
                </div>
              </div>
            </div>

            <div className="xp-modal-header">
              <h2 className="xp-modal-title">Canjear XP</h2>
              <button className="xp-modal-close" onClick={() => setShowXpModal(false)} data-testid="xp-modal-close" aria-label="Cerrar">×</button>
            </div>
            <div className="xp-modal-subheader">
              <span className="xp-modal-badge">XP</span>
              <span className="xp-modal-tus">Tus XP: <strong>{xpStatus.xp}</strong></span>
            </div>
            <p className="xp-modal-sub">Canjea tus puntos por tickets o premios</p>

            <div className="xp-modal-list" data-testid="xp-modal-list">
              {xpCanjesLoading && (
                <div className="xp-tier-row" data-testid="xp-loading">
                  <div className="xp-tier-info" style={{ textAlign: 'center', padding: '16px 0' }}>
                    <div className="xp-tier-name">Cargando canjes…</div>
                  </div>
                </div>
              )}
              {!xpCanjesLoading && (xpCanjesError || xpCanjes.length === 0) && (
                <div className="xp-tier-row" data-testid="xp-empty">
                  <div className="xp-tier-info" style={{ textAlign: 'center', padding: '16px 0' }}>
                    <div className="xp-tier-name">Canjes no disponibles por ahora</div>
                  </div>
                </div>
              )}
              {!xpCanjesLoading && !xpCanjesError && xpCanjes.map((canje) => {
                const costoXp = Number(canje.costo_xp) || 0;
                const nombre = canje.nombre || canje.descripcion || `${costoXp} XP`;
                const available = (xpStatus.xp || 0) >= costoXp;
                const missing = Math.max(0, costoXp - (xpStatus.xp || 0));
                return (
                  <div
                    key={canje.id}
                    className={`xp-tier-row ${available ? 'xp-tier-available' : 'xp-tier-locked'}`}
                    data-testid={`xp-tier-${canje.id}`}
                  >
                    <div className="xp-tier-icon">
                      {available ? (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <rect x="3" y="7" width="18" height="10" rx="2" stroke="#c4b5fd" strokeWidth="2"/>
                          <circle cx="8" cy="12" r="1.5" fill="#c4b5fd"/>
                          <circle cx="16" cy="12" r="1.5" fill="#c4b5fd"/>
                        </svg>
                      ) : (
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                          <rect x="5" y="11" width="14" height="9" rx="1.5" stroke="#c4b5fd" strokeWidth="2"/>
                          <path d="M8 11V7a4 4 0 018 0v4" stroke="#c4b5fd" strokeWidth="2"/>
                        </svg>
                      )}
                    </div>
                    <div className="xp-tier-info">
                      <div className="xp-tier-cost">{costoXp} XP</div>
                      <div className="xp-tier-name">{nombre}</div>
                    </div>
                    <div className="xp-tier-status">
                      {available ? (
                        <>
                          <span className="xp-available-dot">● Disponible</span>
                          <button
                            className={`xp-canjear-row-btn ${claimingXpTier === costoXp ? 'is-claiming' : ''}`}
                            data-testid={`btn-canjear-${canje.id}`}
                            disabled={claimingXpTier !== null}
                            onClick={() => handleClaimXp(costoXp, nombre)}
                          >
                            {claimingXpTier === costoXp ? 'Canjeando…' : 'Canjear'}
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className="xp-canjear-row-btn xp-canjear-locked"
                          data-testid={`btn-canjear-${canje.id}`}
                          disabled
                          aria-disabled="true"
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" style={{ marginRight: '5px', verticalAlign: '-2px' }}>
                            <rect x="5" y="11" width="14" height="9" rx="1.5" stroke="currentColor" strokeWidth="2.2"/>
                            <path d="M8 11V7a4 4 0 018 0v4" stroke="currentColor" strokeWidth="2.2"/>
                          </svg>
                          Faltan {missing} XP
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Aviso flotante "+N XP" tras un giro - estilo gaming loot */}
      {xpToast && (
        <div className="xp-loot-toast" key={xpToast.key} data-testid="xp-floating-toast">
          {/* Chevrons hacia arriba */}
          <div className="xp-loot-chevrons" aria-hidden="true">
            <svg className="xp-chev-svg" viewBox="0 0 36 28" width="36" height="28">
              <path
                d="M3 22 L18 8 L33 22"
                fill="none"
                stroke="#A064FF"
                strokeWidth="7"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <svg className="xp-chev-svg xp-chev-svg-2" viewBox="0 0 36 28" width="36" height="28">
              <path
                d="M3 22 L18 8 L33 22"
                fill="none"
                stroke="#A064FF"
                strokeWidth="7"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          {/* Badge principal */}
          <div className="xp-loot-badge">
            {/* Barra delgada con clip-path triangular (queda detrás del hex) */}
            <div className="xp-loot-bar" aria-hidden="true">
              <span className="xp-loot-shine" aria-hidden="true"></span>
            </div>

            {/* Hexágono XP a la izquierda (sobresale arriba y abajo de la barra) */}
            <div className="xp-loot-hex">
              <svg viewBox="0 0 100 110" width="60" height="66" aria-hidden="true">
                <defs>
                  <linearGradient id="xpHexGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#A064FF" />
                    <stop offset="100%" stopColor="#7C3AED" />
                  </linearGradient>
                </defs>
                <polygon
                  points="50,4 94,30 94,80 50,106 6,80 6,30"
                  fill="url(#xpHexGrad)"
                  stroke="#D8B4FE"
                  strokeWidth="6"
                  strokeLinejoin="round"
                />
              </svg>
              <span className="xp-loot-hex-label">XP</span>
            </div>

            {/* Texto +N XP */}
            <div className="xp-loot-text">
              <span className="xp-loot-plus">+{xpToast.xp}</span>
              <span className="xp-loot-unit">XP</span>
            </div>
          </div>

          {/* Partículas */}
          <span className="xp-loot-particle p1" aria-hidden="true"></span>
          <span className="xp-loot-particle p2" aria-hidden="true"></span>
          <span className="xp-loot-particle p3" aria-hidden="true"></span>
          <span className="xp-loot-particle p4" aria-hidden="true"></span>
          <span className="xp-loot-particle p5" aria-hidden="true"></span>
          <span className="xp-loot-particle p6" aria-hidden="true"></span>
          <span className="xp-loot-particle p7 amber" aria-hidden="true"></span>
          <span className="xp-loot-particle p8 amber" aria-hidden="true"></span>
          <span className="xp-loot-particle p9" aria-hidden="true"></span>
          <span className="xp-loot-particle p10" aria-hidden="true"></span>
          <span className="xp-loot-particle p11" aria-hidden="true"></span>
          <span className="xp-loot-particle p12" aria-hidden="true"></span>
          <span className="xp-loot-particle p13 amber" aria-hidden="true"></span>
          <span className="xp-loot-particle p14 amber" aria-hidden="true"></span>
          <span className="xp-loot-spark s1" aria-hidden="true"></span>
          <span className="xp-loot-spark s2" aria-hidden="true"></span>
          <span className="xp-loot-spark s3" aria-hidden="true"></span>
          <span className="xp-loot-spark s4" aria-hidden="true"></span>
        </div>
      )}
      {/* Toast gaming del canje XP */}
      {claimToast && (
        <div
          className={`claim-toast claim-toast-${claimToast.variant}`}
          key={claimToast.key}
          role="status"
          aria-live="polite"
          data-testid="claim-toast"
        >
          {/* Icono izquierdo: check dentro de círculo con anillos y glow */}
          <div className="claim-toast-left">
            {claimToast.variant === 'error' || claimToast.variant === 'warn' ? (
              <div className={`claim-toast-ring ${claimToast.variant}`}>
                <svg viewBox="0 0 48 48" width="34" height="34" fill="none" aria-hidden="true">
                  {claimToast.variant === 'warn' ? (
                    <>
                      <path d="M24 10 L40 38 L8 38 Z" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinejoin="round"/>
                      <rect x="22" y="20" width="4" height="10" rx="2" fill="currentColor"/>
                      <circle cx="24" cy="34" r="2" fill="currentColor"/>
                    </>
                  ) : (
                    <>
                      <circle cx="24" cy="24" r="16" fill="none" stroke="currentColor" strokeWidth="3.5"/>
                      <path d="M16 16 L32 32 M32 16 L16 32" stroke="currentColor" strokeWidth="4" strokeLinecap="round"/>
                    </>
                  )}
                </svg>
              </div>
            ) : (
              <div className="claim-toast-ring success">
                <span className="claim-toast-ring-outer" aria-hidden="true"></span>
                <svg viewBox="0 0 48 48" width="38" height="38" fill="none" aria-hidden="true">
                  <path
                    d="M12 25 L21 33 L36 16"
                    stroke="currentColor" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" fill="none"
                  />
                </svg>
              </div>
            )}
          </div>

          {/* Texto central */}
          <div className="claim-toast-text">
            <div className="claim-toast-title">{claimToast.title}</div>
            <div className="claim-toast-sub">{claimToast.subtitle}</div>
          </div>

          {/* Icono derecho: premio recibido (solo en success/money) */}
          {(claimToast.variant === 'success' || claimToast.variant === 'money') && claimToast.rewardKind && (
            <div className={`claim-toast-right kind-${claimToast.rewardKind}`}>
              {claimToast.rewardKind === 'ticket' && (
                <svg viewBox="0 0 48 48" width="40" height="40" fill="none" aria-hidden="true">
                  <path d="M6 16 a4 4 0 014-4 h28 a4 4 0 014 4 v4 a3 3 0 000 6 v4 a4 4 0 01-4 4 h-28 a4 4 0 01-4-4 v-4 a3 3 0 000-6 v-4 z"
                    fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinejoin="round"/>
                  <circle cx="19" cy="22" r="1.8" fill="currentColor"/>
                  <circle cx="29" cy="26" r="1.8" fill="currentColor"/>
                </svg>
              )}
              {claimToast.rewardKind === 'golden' && (
                <svg viewBox="0 0 48 48" width="40" height="40" fill="none" aria-hidden="true">
                  <path d="M24 6L29 18L42 19L32 28L35 41L24 34L13 41L16 28L6 19L19 18L24 6Z"
                    fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinejoin="round"/>
                </svg>
              )}
              {claimToast.rewardKind === 'spin' && (
                <svg viewBox="0 0 48 48" width="40" height="40" fill="none" aria-hidden="true">
                  <circle cx="24" cy="24" r="14" fill="none" stroke="currentColor" strokeWidth="3.5"
                    strokeDasharray="60 18" transform="rotate(-90 24 24)"/>
                  <path d="M34 18 L40 14 L36 22 Z" fill="currentColor"/>
                  <circle cx="24" cy="24" r="3.5" fill="currentColor"/>
                </svg>
              )}
              {claimToast.rewardKind === 'money' && (
                <svg viewBox="0 0 48 48" width="40" height="40" fill="none" aria-hidden="true">
                  <circle cx="24" cy="24" r="15" fill="none" stroke="currentColor" strokeWidth="3"/>
                  <text x="24" y="30" textAnchor="middle" fontSize="18" fontWeight="900" fill="currentColor" fontFamily="system-ui">$</text>
                </svg>
              )}
              {claimToast.rewardKind === 'xp' && (
                <svg viewBox="0 0 48 48" width="40" height="40" fill="none" aria-hidden="true">
                  <polygon points="24,6 40,14 40,34 24,42 8,34 8,14"
                    fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinejoin="round"/>
                  <text x="24" y="30" textAnchor="middle" fontSize="13" fontWeight="900" fill="currentColor" fontFamily="system-ui">XP</text>
                </svg>
              )}
            </div>
          )}

          {/* Esquinas con luz */}
          <span className="claim-toast-corner tl" aria-hidden="true"></span>
          <span className="claim-toast-corner tr" aria-hidden="true"></span>
          <span className="claim-toast-corner bl" aria-hidden="true"></span>
          <span className="claim-toast-corner br" aria-hidden="true"></span>
          <span className="claim-toast-corner ml" aria-hidden="true"></span>
          <span className="claim-toast-corner mr" aria-hidden="true"></span>

          {/* Partículas sutiles */}
          <span className="claim-toast-p p1" aria-hidden="true"></span>
          <span className="claim-toast-p p2 green" aria-hidden="true"></span>
          <span className="claim-toast-p p3" aria-hidden="true"></span>
          <span className="claim-toast-p p4 green" aria-hidden="true"></span>
          <span className="claim-toast-p p5" aria-hidden="true"></span>
          <span className="claim-toast-p p6 green" aria-hidden="true"></span>
          <span className="claim-toast-p p7" aria-hidden="true"></span>
          <span className="claim-toast-p p8 green" aria-hidden="true"></span>
          <span className="claim-toast-p p9" aria-hidden="true"></span>
          <span className="claim-toast-p p10 green" aria-hidden="true"></span>
          <span className="claim-toast-p p11" aria-hidden="true"></span>
          <span className="claim-toast-p p12 green" aria-hidden="true"></span>
          <span className="claim-toast-p p13 green" aria-hidden="true"></span>
          <span className="claim-toast-p p14" aria-hidden="true"></span>
          <span className="claim-toast-p p15" aria-hidden="true"></span>
          <span className="claim-toast-p p16 green" aria-hidden="true"></span>
          <span className="claim-toast-p p17 green" aria-hidden="true"></span>
          <span className="claim-toast-p p18" aria-hidden="true"></span>
          <span className="claim-toast-p p19" aria-hidden="true"></span>
          <span className="claim-toast-p p20 green" aria-hidden="true"></span>
          <span className="claim-toast-p p21 green" aria-hidden="true"></span>
          <span className="claim-toast-p p22" aria-hidden="true"></span>
          <span className="claim-toast-p p23" aria-hidden="true"></span>
          <span className="claim-toast-p p24 green" aria-hidden="true"></span>
          <span className="claim-toast-p p25 green" aria-hidden="true"></span>
          <span className="claim-toast-p p26" aria-hidden="true"></span>
        </div>
      )}

      {showLogoutConfirm && (
        <div
          className="logout-confirm-overlay"
          onClick={() => setShowLogoutConfirm(false)}
          data-testid="logout-confirm-overlay"
        >
          <div
            className="logout-confirm-card"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="logout-confirm-title"
          >
            <div className="logout-confirm-icon" aria-hidden="true">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
            </div>
            <h3 id="logout-confirm-title" className="logout-confirm-title">
              ¿Seguro que quieres salir?
            </h3>
            <p className="logout-confirm-text">
              Perderás la vista actual de la ruleta, pero tus tickets y premios se conservan.
            </p>
            <div className="logout-confirm-actions">
              <button
                type="button"
                className="logout-confirm-btn logout-confirm-btn--cancel"
                onClick={() => setShowLogoutConfirm(false)}
                data-testid="logout-confirm-cancel"
              >
                Cancelar
              </button>
              <button
                type="button"
                className="logout-confirm-btn logout-confirm-btn--exit"
                onClick={() => {
                  setShowLogoutConfirm(false);
                  logout();
                }}
                data-testid="logout-confirm-exit"
              >
                Salir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Ruleta;
